import { useState, useEffect, useRef  } from "react";
import * as XLSX from "xlsx";


// ── STATIC DATA ───────────────────────────────────────────────────────────────

const DEFAULT_EVENTS = {
  Performance: { icon:"🎭", color:"#C9A84C", basePrice:3600, extraPerHour:1800, gst:0.18,
    included:["Main stage access","PA system & monitors","Full lighting rig","Green room (2 rooms)","Front-of-house area","Security staff (2)","Pre & post-event cleaning"],
    excluded:["Costumes & props","Catering services","Photography / videography","Additional musicians","Marketing & ticketing"] },
  Workshop:    { icon:"🎓", color:"#5B9BD5", basePrice:1600, extraPerHour:800,  gst:0.18,
    included:["Studio space (cap. 30 pax)","Tables & chairs","Whiteboard & markers","PA system & mic","Air conditioning & Wi-Fi","Clean restrooms"],
    excluded:["Projector & screen","Stationery & printouts","Catering","Parking (limited)","Special FX lighting"] },
  Rehearsal:   { icon:"🎵", color:"#6BAE75", basePrice:1000, extraPerHour:500,  gst:0.18,
    included:["Practice studio","Basic PA system","Full-length mirrors","Changing area","Basic lighting"],
    excluded:["Full stage lighting rig","Recording equipment","Costumes & props","Catering","Stage crew"] },
  Shoot:       { icon:"🎬", color:"#E87070", basePrice:3600, extraPerHour:1800, gst:0.18,
    included:["2000 sq ft open space","Cyclorama wall","Basic lighting package","Generator backup","Changing & makeup room","Parking for 5 crew vehicles"],
    excluded:["Camera equipment","Crew members","Specialized lighting","Catering","Styling & makeup artists"] },
  Corporate:   { icon:"🏛", color:"#9B59B6", basePrice:4500, extraPerHour:1200, gst:0.18,
    included:["Main hall access","PA system & microphone","Projector & screen","Tables & chairs setup","Air conditioning & Wi-Fi","Whiteboard & markers","Security staff","Pre & post-event cleaning"],
    excluded:["Catering services","Photography / videography","Stationery & printouts","Parking (limited)","Decorations & floral arrangements"] },
};

const DEFAULT_CAPACITY = [
  {id:"c1",format:"Standup Comedy",capacity:80},{id:"c2",format:"Music Performance",capacity:60},
  {id:"c3",format:"Dance Performance",capacity:45},{id:"c4",format:"Open Mic",capacity:60},
  {id:"c5",format:"Theatre",capacity:45},{id:"c6",format:"Workshop",capacity:30},
  {id:"c7",format:"Private Conference",capacity:60},{id:"c8",format:"Small Team Meeting",capacity:20},
];

const DEFAULT_PERF_TYPES = [
  {id:"pt1",name:"Standup Comedy",capacity:75},{id:"pt2",name:"Music Performance",capacity:60},
  {id:"pt3",name:"Dance Performance",capacity:45},{id:"pt4",name:"Theatre",capacity:45},
  {id:"pt5",name:"Poetry & Storytelling",capacity:60},{id:"pt6",name:"Open Mic",capacity:60},
  {id:"pt7",name:"Magic & Mentalism",capacity:55},{id:"pt8",name:"Puppet Show",capacity:55},
];

const DEFAULT_TECH = {
  Sound:["2 Tops","2 Monitors","1 SM 58 & SM 57 Mic","4 JTS Mics","4 Instrument plugins","Mixer - Studiomaster AIR 12U (with basic effects)","No DI Box or Phantom Power in Mixer","3 Small and 3 Big Mic Stands","1 Cordless Mic"],
  "Light Setup":["6 Track Lights — 2 RGB, 2 Warm, 2 White","DMX Light Controller"],
  Others:["AC — Two 2 ton","Power backup: 2-4 hours (without AC)","Smoke Machine (for shoots only)"],
};

const ACTIVITIES = [
  {icon:"🎭",label:"Performances",        desc:"Live shows, concerts, recitals & cultural evenings"},
  {icon:"🎓",label:"Workshops",           desc:"Training sessions, masterclasses & skill development"},
  {icon:"🎵",label:"Rehearsals",          desc:"Practice sessions for soloists, bands & ensembles"},
  {icon:"🎬",label:"Shoots",              desc:"Photo & video productions, brand content & films"},
  {icon:"🏛", label:"Corporate & Private", desc:"Conferences, seminars, team offsites & private events"},
  {icon:"👥",label:"Small Team Meetings", desc:"Focused sessions, offsites & creative brainstorms"},
];

// Policy text — defined once at module level, not recreated on every render
const BOOKING_POLICIES = [
  "The venue booking duration must include setup, sound/light check, rehearsal, event time, and pack-up.",
  "No additional complimentary time will be provided before or after the booked slot for setup or dismantling.",
  "Venue timing will be considered strictly from the confirmed booking start time, irrespective of the actual event start time.",
  "Final venue charges will be calculated based on the booking start time and out-time.",
];
const CANCELLATION_POLICIES = [
  "50% cancellation charges will apply if the booking is cancelled up to 20 days prior to the event date.",
  "Cancellations made within 20 days of the event will incur 100% cancellation charges.",
];

// ── UTILS ─────────────────────────────────────────────────────────────────────

const HALF_HOURS       = Array.from({length:31},(_,i)=>8+i*0.5); // 8:00 to 23:00 in 30-min steps
const HALF_HOURS_START = HALF_HOURS.filter(h=>h<=21);             // 8:00 AM to 9:00 PM max start
const HALF_HOURS_END   = HALF_HOURS.filter(h=>h>=10);             // 10:00 AM to 11:00 PM min end
const pad         = h => { const hr=Math.floor(h); const min=h%1===0.5?"30":"00"; return String(hr).padStart(2,"0")+min; };
const isoTime     = h => { const hr=Math.floor(h); const min=h%1===0.5?"30":"00"; return `${String(hr).padStart(2,"0")}:${min}:00`; };
const fmtH        = h => { const hr=Math.floor(h); const min=h%1===0.5?":30":":00"; if(hr===12) return `12${min} PM`; return hr<12?`${hr}${min} AM`:`${hr-12}${min} PM`; };
const todayStr    = () => { const d=new Date(); return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}-${String(d.getDate()).padStart(2,"0")}`; };
const minBookableDate = () => { const d=new Date(); d.setDate(d.getDate()+2); return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}-${String(d.getDate()).padStart(2,"0")}`; };
const isWeekend   = ds => { const d=new Date(ds+"T00:00:00"); return d.getDay()===0||d.getDay()===6; };
const overlaps    = (s1,e1,s2,e2) => s1<e2&&e1>s2;
const fmtDate     = ds => new Date(ds+"T00:00:00").toLocaleDateString("en-IN",{weekday:"long",day:"numeric",month:"long",year:"numeric"});
const fmtShort    = ds => new Date(ds+"T00:00:00").toLocaleDateString("en-IN",{weekday:"short",day:"numeric",month:"short",year:"numeric"});
const fmtDuration = h => { const hr=Math.floor(h); const min=h%1===0.5?" 30 min":""; return `${hr} hr${hr!==1?"s":""}${min}`; };
const calcPrice   = (type,hours,pricing) => {
  const ev=DEFAULT_EVENTS[type], p=(pricing&&pricing[type])||ev;
  const base=p.basePrice+Math.max(0,hours-2)*p.extraPerHour, gst=Math.round(base*ev.gst);
  return {base,gst,total:base+gst};
};

// ── STORAGE ───────────────────────────────────────────────────────────────────

const loadData = async (key, fallback = []) => {
  try {
    const res = await fetch("/api/admin-data", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ key }),
    });
    const data = await res.json();
    return (data.value !== null && data.value !== undefined) ? data.value : fallback;
  } catch(e) { return fallback; }
};

const saveData = async (key, val) => {
  try {
    await fetch("/api/admin-save", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ key, value: val }),
    });
  } catch(e) {}
};

const loadFacilities  = async () => { const s=await loadData("vb_fac",null); return (s&&typeof s==="object"&&!Array.isArray(s))?s:Object.fromEntries(Object.entries(DEFAULT_EVENTS).map(([k,v])=>[k,{included:[...v.included],excluded:[...v.excluded]}])); };
const loadCapacity    = async () => { const s=await loadData("vb_cap",null); return (Array.isArray(s)&&s.length>0)?s:DEFAULT_CAPACITY.map(c=>({...c})); };
const loadPerfTypes   = async () => { const s=await loadData("vb_ptypes",null); return (Array.isArray(s)&&s.length>0)?s:DEFAULT_PERF_TYPES.map(p=>({...p})); };
const loadPricing     = async () => { const s=await loadData("vb_pricing",null); return (s&&typeof s==="object"&&!Array.isArray(s))?s:Object.fromEntries(Object.entries(DEFAULT_EVENTS).map(([k,v])=>[k,{basePrice:v.basePrice,extraPerHour:v.extraPerHour}])); };
const loadTech        = async () => { const s=await loadData("vb_tech",null); return (s&&typeof s==="object"&&!Array.isArray(s))?s:Object.fromEntries(Object.entries(DEFAULT_TECH).map(([k,v])=>[k,[...v]])); };
const loadGCalConfig  = async () => { const s=await loadData("vb_gcal",null); return (s&&typeof s==="object"&&!Array.isArray(s))?s:{webhookUrl:"",calendarId:"",adminEmail:""}; };
const loadHeroImage   = async () => { const s=await loadData("vb_hero",""); return typeof s==="string"?s:""; };
const loadLogoImage   = async () => { const s=await loadData("vb_logo",""); return typeof s==="string"?s:""; };
const loadCarousel    = async () => { const s=await loadData("vb_carousel",null); return Array.isArray(s)?s:[]; };
const loadDiscounts   = async () => { const s=await loadData("vb_discounts",null); return Array.isArray(s)?s:[]; };

const hashCode = async str => {
  const buf = await crypto.subtle.digest("SHA-256", new TextEncoder().encode(str.toUpperCase().trim()));
  return Array.from(new Uint8Array(buf)).map(b=>b.toString(16).padStart(2,"0")).join("");
};

// ── GCAL HELPERS ──────────────────────────────────────────────────────────────

const VENUE_LOCATION="Artkhoj, 1st Floor, 31/2, 22nd Main Rd, R.K Colony, Marenahalli, 2nd Phase, J. P. Nagar, Bengaluru, Karnataka 560078, India";

const makeGCalUrl = bk => {
  const d=bk.date.replace(/-/g,"");
  const evLabel=(bk.eventType==="Performance"&&bk.perfTypeName)?bk.perfTypeName:bk.eventType;
  const title=encodeURIComponent(`${evLabel} — ${bk.customerName}`);
  const desc=encodeURIComponent(`Booking ID: ${bk.id}\nEvent: ${evLabel}\nCustomer: ${bk.customerName}\nPhone: ${bk.customerPhone}\n\n${VENUE_LOCATION}`);
  const loc=encodeURIComponent(VENUE_LOCATION);
  return `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${title}&dates=${d}T${pad(bk.start)}00/${d}T${pad(bk.end)}00&ctz=Asia%2FKolkata&details=${desc}&location=${loc}`;
};

const syncBlockToGCal = (block, cfg) => {
  if(!cfg?.webhookUrl) return;
  if(block.isBuffer||block.reason?.toLowerCase().includes("buffer")) return;
  const startISO=`${block.date}T${isoTime(block.allDay?8:block.start)}+05:30`;
  const endISO=`${block.date}T${isoTime(block.allDay?23:block.end)}+05:30`;
  const title=block.reason;
  fetch(cfg.webhookUrl,{
   method:"POST", mode:"no-cors",
    body:JSON.stringify({
      action:"block",
      calendarId:cfg.calendarId||"",
      adminEmail:cfg.adminEmail||"",
      title,
      startISO, endISO,
      date:block.date,
      allDay:block.allDay,
      reason:block.reason,
      comment:block.comment||"",
      location:VENUE_LOCATION,
      dateFormatted:new Date(block.date+"T00:00:00").toLocaleDateString("en-IN",{weekday:"long",day:"numeric",month:"long",year:"numeric"}),
      timeFormatted:block.allDay?"Full day":`${fmtH(block.start)} – ${fmtH(block.end)}`,
      description:`Blocked by Admin.\nDate: ${block.date}\n${block.allDay?"Full day":"Time: "+fmtH(block.start)+" – "+fmtH(block.end)}\nDetails: ${block.reason}${block.comment?"\nComments: "+block.comment:""}`,
    })
  }).catch(()=>{});
};


const syncResendEmail = (bk, cfg, perfTypes) => {
  if(!cfg?.webhookUrl||!bk.customerEmail) return;
  const evLabel=(bk.eventType==="Performance"&&bk.perfTypeName)?bk.perfTypeName:bk.eventType;
  const hrs=bk.end-bk.start;
  const dur=`${Math.floor(hrs)} hr${Math.floor(hrs)!==1?"s":""}${hrs%1===0.5?" 30 min":""}`;
  const found=perfTypes?.find(p=>p.id===bk.perfTypeId);
  fetch(cfg.webhookUrl,{ method:"POST", mode:"no-cors", body:JSON.stringify({
    action:"resend_email",
    bookingId:bk.id, eventType:evLabel,
    dateFormatted:new Date(bk.date+"T00:00:00").toLocaleDateString("en-IN",{weekday:"long",day:"numeric",month:"long",year:"numeric"}),
    timeFormatted:`${fmtH(bk.start)} – ${fmtH(bk.end)} (${dur})`,
    perfType:found?.name||"", seatingCapacity:found?.capacity||"",
    customerName:bk.customerName, customerEmail:bk.customerEmail, customerPhone:bk.customerPhone,
    totalAmount:bk.total?.toLocaleString("en-IN")||"",
    location:VENUE_LOCATION,
  })}).catch(()=>{});
};

const syncDeleteToGCal = (bk, cfg) => {
  if(!cfg?.webhookUrl) return;
  const evLabel=(bk.eventType==="Performance"&&bk.perfTypeName)?bk.perfTypeName:bk.eventType;
  fetch(cfg.webhookUrl,{ method:"POST", mode:"no-cors", body:JSON.stringify({
    action:"delete_calendar",
    calendarId:cfg.calendarId||"",
    adminEmail:cfg.adminEmail||"",
    bookingId:bk.id,
    title:`${evLabel} @ Artkhoj — ${bk.customerName}`,
    startISO:`${bk.date}T${isoTime(bk.start)}+05:30`,
    endISO:`${bk.date}T${isoTime(bk.end)}+05:30`,
    dateFormatted:new Date(bk.date+"T00:00:00").toLocaleDateString("en-IN",{weekday:"long",day:"numeric",month:"long",year:"numeric"}),
    timeFormatted:`${fmtH(bk.start)} – ${fmtH(bk.end)}`,
    eventType:evLabel,
    customerName:bk.customerName, customerEmail:bk.customerEmail||"", customerPhone:bk.customerPhone||"",
    totalAmount:bk.total?.toLocaleString("en-IN")||"",
    eventDesc:bk.eventDescription||"",
  })}).catch(()=>{});
};


const syncCancelToGCal = (bk, cfg, perfTypes) => {
  if(!cfg?.webhookUrl) return;
  const evLabel=(bk.eventType==="Performance"&&bk.perfTypeName)?bk.perfTypeName:bk.eventType;
  const found=perfTypes?.find(p=>p.id===bk.perfTypeId);
  fetch(cfg.webhookUrl,{ method:"POST", mode:"no-cors", body:JSON.stringify({
    action:"cancel",
    calendarId:cfg.calendarId||"", adminEmail:cfg.adminEmail||"",
    bookingId:bk.id,
    originalTitle:`${evLabel} @ Artkhoj — ${bk.customerName}`,
    cancelledTitle:`CANCELLED — ${evLabel} @ Artkhoj — ${bk.customerName}`,
    startISO:`${bk.date}T${isoTime(bk.start)}+05:30`,
    endISO:`${bk.date}T${isoTime(bk.end)}+05:30`,
    dateFormatted:new Date(bk.date+"T00:00:00").toLocaleDateString("en-IN",{weekday:"long",day:"numeric",month:"long",year:"numeric"}),
    timeFormatted:`${fmtH(bk.start)} – ${fmtH(bk.end)}`,
    eventType:evLabel, perfType:found?.name||"",
    customerName:bk.customerName, customerEmail:bk.customerEmail, customerPhone:bk.customerPhone,
    totalAmount:bk.total?.toLocaleString("en-IN")||"",
    eventDesc:bk.eventDescription||"",
  })}).catch(()=>{});
};

const syncToGCal = (bk,cfg,perfTypes) => {
  if(!cfg?.webhookUrl) return;
  const found=perfTypes?.find(p=>p.id===bk.perfTypeId);
  const evLabel=(bk.eventType==="Performance"&&bk.perfTypeName)?bk.perfTypeName:bk.eventType;
  fetch(cfg.webhookUrl, { method:"POST", mode:"no-cors", body:JSON.stringify({
    action:"booking", calendarId:cfg.calendarId||"", adminEmail:cfg.adminEmail||"",
    bookingId:bk.id, title:`${evLabel} — ${bk.customerName}`,
    date:bk.date, startHour:bk.start, endHour:bk.end,
    startISO:`${bk.date}T${isoTime(bk.start)}+05:30`, endISO:`${bk.date}T${isoTime(bk.end)}+05:30`,
    dateFormatted:new Date(bk.date+"T00:00:00").toLocaleDateString("en-IN",{weekday:"long",day:"numeric",month:"long",year:"numeric"}),
    timeFormatted:`${fmtH(bk.start)} – ${fmtH(bk.end)} (${fmtDuration(bk.end-bk.start)})`,
    eventType:evLabel, perfType:found?.name||"", seatingCapacity:found?.capacity||"",
    location:VENUE_LOCATION,
    customerName:bk.customerName, customerEmail:bk.customerEmail, customerPhone:bk.customerPhone,
    paymentMethod:bk.paymentMethod||"", totalAmount:bk.total?.toLocaleString("en-IN")||"",
    billingName:bk.billing?.name||"", billingGST:bk.billing?.gstNo||"", eventDesc:bk.eventDescription||"",
    description:`Booking ID: ${bk.id}\nEvent: ${evLabel}\nCustomer: ${bk.customerName}\nEmail: ${bk.customerEmail}\nPhone: ${bk.customerPhone}${bk.eventDescription?"\n\n"+bk.eventDescription:""}`,
  })}).catch(()=>{});
};

// ── BUFFER HELPERS ────────────────────────────────────────────────────────────

const isSlotOccupied = (date,s,e,bkList,blList) => {
  for(const bk of bkList){ if(bk.date===date&&s<bk.end&&e>bk.start) return true; }
  for(const bl of blList){ if(bl.date===date&&(bl.allDay||(s<bl.end&&e>bl.start))) return true; }
  return false;
};

const applyBuffer = (bk,allBookings,allBlocked) => {
  const result=[...allBlocked];
  if(bk.start-1>=8){ const s=bk.start-1,e=bk.start; if(!isSlotOccupied(bk.date,s,e,allBookings,result)) result.push({id:"BUF_"+bk.id+"_pre",date:bk.date,start:s,end:e,allDay:false,reason:"Buffer — pre booking",isBuffer:true,bookingId:bk.id}); }
  if(bk.end+1<=23){  const s=bk.end,  e=bk.end+1;   if(!isSlotOccupied(bk.date,s,e,allBookings,result)) result.push({id:"BUF_"+bk.id+"_post",date:bk.date,start:s,end:e,allDay:false,reason:"Buffer — post booking",isBuffer:true,bookingId:bk.id}); }
  return result;
};

// ── STYLES ────────────────────────────────────────────────────────────────────

const S = {
  cal:      {background:"#141414",border:"1px solid #2a2a2a",borderRadius:10,padding:20,marginBottom:24},
  calNav:   {background:"none",border:"1px solid #2a2a2a",color:"#E8E0D0",width:32,height:32,borderRadius:6,cursor:"pointer",fontSize:20,display:"flex",alignItems:"center",justifyContent:"center"},
  calDName: {textAlign:"center",fontSize:11,color:"#3a3530",padding:4},
  calDay:   {background:"none",border:"1px solid transparent",color:"#E8E0D0",width:36,height:36,borderRadius:6,cursor:"pointer",fontSize:13,fontFamily:"'Outfit',sans-serif",display:"flex",alignItems:"center",justifyContent:"center",margin:"0 auto"},
  calSel:   {background:"#C9A84C",color:"#000",fontWeight:700},
  calDis:   {color:"#2a2420",cursor:"not-allowed"},
  slot:     {background:"#141414",border:"1px solid #2a2a2a",color:"#E8E0D0",padding:"8px 14px",borderRadius:6,fontSize:13,cursor:"pointer",fontFamily:"'Outfit',sans-serif"},
  slotSel:  {background:"#C9A84C",borderColor:"#C9A84C",color:"#000",fontWeight:700},
  slotDis:  {color:"#2a2420",cursor:"not-allowed",textDecoration:"line-through"},
  eCard:    {background:"#141414",border:"2px solid #2a2a2a",borderRadius:10,padding:20,cursor:"pointer",textAlign:"left",position:"relative",overflow:"hidden",fontFamily:"'Outfit',sans-serif"},
  eCardDis: {opacity:0.35,cursor:"not-allowed"},
  box:      {background:"#141414",border:"1px solid #2a2a2a",borderRadius:10,padding:20,marginBottom:20},
  row:      {display:"flex",justifyContent:"space-between",alignItems:"center",fontSize:14,marginBottom:10},
  lbl:      {fontSize:11,fontWeight:700,letterSpacing:"0.1em",textTransform:"uppercase",color:"#5a5244",marginBottom:10},
  inp:      {display:"block",width:"100%",background:"#0a0a0a",border:"1px solid #2a2a2a",color:"#E8E0D0",padding:"10px 14px",borderRadius:6,fontSize:14,fontFamily:"'Outfit',sans-serif",marginBottom:10,outline:"none"},
  badge:    {display:"inline-block",background:"#141414",border:"1px solid #2a2a2a",padding:"6px 14px",borderRadius:20,fontSize:13,color:"#C9A84C",marginBottom:18},
  sHead:    {fontFamily:"'Cormorant Garamond',serif",fontSize:30,fontWeight:600,marginBottom:20,letterSpacing:"0.02em"},
  h3:       {fontFamily:"'Cormorant Garamond',serif",fontSize:22,margin:"24px 0 14px",color:"#E8E0D0"},
  btnP:     {background:"#C9A84C",border:"none",color:"#000",padding:"11px 28px",borderRadius:7,fontSize:14,fontWeight:700,cursor:"pointer",fontFamily:"'Outfit',sans-serif",letterSpacing:"0.03em"},
  btnS:     {background:"none",border:"1px solid #2a2a2a",color:"#6b6356",padding:"11px 28px",borderRadius:7,fontSize:14,cursor:"pointer",fontFamily:"'Outfit',sans-serif"},
  btnDis:   {opacity:0.38,cursor:"not-allowed"},
  empty:    {color:"#3a3530",fontSize:14,padding:28,textAlign:"center",background:"#141414",borderRadius:8,border:"1px dashed #2a2a2a"},
  arrBtn:   dis=>({background:"none",border:"none",cursor:dis?"default":"pointer",color:dis?"#2a2a2a":"#5a5244",fontSize:9,lineHeight:1,padding:"2px 4px"}),
  eyebrow:  {fontSize:11,fontWeight:700,letterSpacing:"0.14em",textTransform:"uppercase",color:"#5a5244",marginBottom:8},
  secHead:  {fontFamily:"'Cormorant Garamond',serif",fontSize:28,fontWeight:600,color:"#E8E0D0",lineHeight:1.2,marginBottom:20},
};

// ── LANDING SECTIONS ──────────────────────────────────────────────────────────

function LandingActivities() {
  return (
    <div style={{marginTop:40}}>
      <div style={S.eyebrow}>What we host</div>
      <h2 style={S.secHead}>A space built for every kind of creator</h2>
      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:12}}>
        {ACTIVITIES.map(a=>(
          <div key={a.label} style={{background:"#141414",border:"1px solid #242424",borderRadius:10,padding:"18px 16px",display:"flex",flexDirection:"column",gap:10}}>
            <span style={{fontSize:26,lineHeight:1}}>{a.icon}</span>
            <div>
              <div style={{fontSize:14,fontWeight:600,color:"#E8E0D0",marginBottom:4}}>{a.label}</div>
              <div style={{fontSize:12,color:"#5a5244",lineHeight:1.55}}>{a.desc}</div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function LandingCapacity({capacity}) {
  if(!capacity||!capacity.length) return null;
  return (
    <div style={{marginTop:40}}>
      <div style={S.eyebrow}>Seating capacity</div>
      <h2 style={S.secHead}>How many can we fit?</h2>
      <div style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:10}}>
        {capacity.map(item=>(
          <div key={item.id} style={{background:"#141414",border:"1px solid #242424",borderRadius:10,padding:"16px 14px"}}>
            <div style={{fontSize:12,color:"#5a5244",lineHeight:1.3,minHeight:32}}>{item.format}</div>
            <div style={{display:"flex",alignItems:"baseline",gap:4,marginTop:6}}>
              <span style={{fontFamily:"'Cormorant Garamond',serif",fontSize:32,fontWeight:700,color:"#C9A84C",lineHeight:1}}>{item.capacity}</span>
              <span style={{fontSize:11,color:"#3a3530"}}>pax</span>
            </div>
          </div>
        ))}
      </div>
      <div style={{fontSize:14,color:"#f3f3f3",marginTop:10,fontStyle:"italic"}}>* Capacity figures are without video shoot setup</div>
    </div>
  );
}

// ── STEP BAR (reusable) ───────────────────────────────────────────────────────

function StepBar({step,steps,idle=false,mb=36}) {
  return (
    <div style={{display:"flex",alignItems:"flex-start",marginBottom:mb,position:"relative"}}>
      <div style={{position:"absolute",top:15,left:"10%",right:"10%",height:1,background:"#2a2a2a",zIndex:0}}/>
      {steps.map((s,i)=>{
        const done=!idle&&step>i, active=!idle&&step===i;
        return (
          <div key={s} style={{flex:1,display:"flex",flexDirection:"column",alignItems:"center",gap:6,zIndex:1}}>
            <div style={{width:30,height:30,borderRadius:"50%",display:"flex",alignItems:"center",justifyContent:"center",fontSize:11,fontWeight:700,background:done?"#6BAE75":active?"#C9A84C":"#141414",border:`1px solid ${done?"#6BAE75":active?"#C9A84C":"#2a2a2a"}`,color:(done||active)?"#000":"#5a5244"}}>
              {done?"✓":i+1}
            </div>
            <div style={{fontSize:10,color:active?"#C9A84C":"#5a5244",letterSpacing:"0.04em",textAlign:"center"}}>{s}</div>
          </div>
        );
      })}
    </div>
  );
}

// ── CALENDAR ──────────────────────────────────────────────────────────────────

function Calendar({selected,onSelect,blocked}) {
  const [v,setV]=useState(()=>{const d=new Date();return{y:d.getFullYear(),m:d.getMonth()};});
  const first=new Date(v.y,v.m,1).getDay(), days=new Date(v.y,v.m+1,0).getDate();
  const min=minBookableDate(), cells=[...Array(first).fill(null),...Array.from({length:days},(_,i)=>i+1)];
  const prev=()=>setV(x=>x.m===0?{y:x.y-1,m:11}:{y:x.y,m:x.m-1});
  const next=()=>setV(x=>x.m===11?{y:x.y+1,m:0}:{y:x.y,m:x.m+1});
  return (
    <div style={S.cal}>
      <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:16}}>
        <button onClick={prev} style={S.calNav}>&#8249;</button>
        <span style={{fontFamily:"'Cormorant Garamond',serif",fontSize:20,fontWeight:600}}>{new Date(v.y,v.m).toLocaleString("default",{month:"long",year:"numeric"})}</span>
        <button onClick={next} style={S.calNav}>&#8250;</button>
      </div>
      <div style={{display:"grid",gridTemplateColumns:"repeat(7,1fr)",gap:4}}>
        {["Su","Mo","Tu","We","Th","Fr","Sa"].map(d=><div key={d} style={S.calDName}>{d}</div>)}
        {cells.map((day,i)=>{
          if(!day) return <div key={"e"+i}/>;
          const ds=`${v.y}-${String(v.m+1).padStart(2,"0")}-${String(day).padStart(2,"0")}`;
          const dis=ds<min||blocked.some(b=>b.date===ds&&!b.isBuffer&&(b.allDay||(b.start<=8&&b.end>=23))), sel=selected===ds;
          return <button key={ds} onClick={()=>!dis&&onSelect(ds)} style={{...S.calDay,...(sel?S.calSel:{}),...(dis?S.calDis:{})}}>{day}</button>;
        })}
      </div>
    </div>
  );
}

// ── TIME SELECTOR ─────────────────────────────────────────────────────────────

function TimeSelector({date,bookings,blocked,onSelect}) {
  const [start,setStart]=useState(null), [end,setEnd]=useState(null);

  // Check if a half-hour slot h is occupied by any booking or block
    const busy=h=>{
    for(const b of blocked) if(b.date===date&&(b.allDay||(h>=b.start&&h<b.end))) return true;
    for(const bk of bookings) if(!bk.cancelled&&bk.date===date&&h>=bk.start&&h<bk.end) return true;
    return false;
  };

  // Check all 30-min slots in range [s, e) are free
  const rangeOk=(s,e)=>{ for(let h=s;h<e-0.001;h+=0.5){if(busy(h))return false;} return true; };

  // Start is ok if it's ≤ 9 PM AND the next 2 hours are fully free
  const okStart=h=>h<=21&&rangeOk(h,h+2);

  // End times: start+2, start+3, start+4... (1-hour steps, same :00/:30 offset as start)
  const validEnds=start!==null
    ? Array.from({length:Math.floor(21-start)+1},(_,i)=>+(start+2+i).toFixed(1)).filter(h=>h<=23)
    : [];

  // End ok if the entire range [start, end) is free
  const okEnd=h=>start!==null&&rangeOk(start,h);

  const pickS=h=>{ setStart(h); setEnd(null); onSelect(h,null); };
  const pickE=h=>{ setEnd(h); onSelect(start,h); };

  const slotBtn=(h,sel,ok,onClick)=>(
    <button key={h} onClick={()=>ok&&onClick()} style={{...S.slot,...(sel?S.slotSel:{}),...(!ok?S.slotDis:{})}}>
      {fmtH(h)}
    </button>
  );

  return (
    <div>
      <div style={{marginBottom:20}}>
        <div style={{fontSize:12,color:"#5a5244",letterSpacing:"0.08em",textTransform:"uppercase",marginBottom:10}}>Select start time</div>
        <div style={{display:"flex",flexWrap:"wrap",gap:8}}>
          {HALF_HOURS_START.map(h=>slotBtn(h,start===h,okStart(h),()=>pickS(h)))}
        </div>
      </div>
      {start!==null&&(
        <div>
          <div style={{fontSize:12,color:"#5a5244",letterSpacing:"0.08em",textTransform:"uppercase",marginBottom:10}}>
            Select end time <span style={{fontSize:11,textTransform:"none",letterSpacing:0}}>(min 2 hrs · max 11 PM)</span>
          </div>
          <div style={{display:"flex",flexWrap:"wrap",gap:8}}>
            {validEnds.map(h=>slotBtn(h,end===h,okEnd(h),()=>pickE(h)))}
          </div>
        </div>
      )}
      {start!==null&&end!==null&&(
        <div style={{marginTop:12,fontSize:13,color:"#C9A84C",fontWeight:600}}>
          &#10003; {fmtH(start)} – {fmtH(end)} &middot; {fmtDuration(end-start)}
        </div>
      )}
    </div>
  );
}

// ── EVENT CARDS ───────────────────────────────────────────────────────────────

function EventCards({date,start,end,selected,onSelect,pricing}) {
  const wknd=isWeekend(date);
  return (
    <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:14,marginBottom:24}}>
      {Object.entries(DEFAULT_EVENTS).map(([key,ev])=>{
        const restricted=["Workshop","Rehearsal"].includes(key)&&wknd&&overlaps(start,end,14,22);
         const sel=selected===key;
        return (
          <button key={key} onClick={()=>!restricted&&onSelect(key)} style={{...S.eCard,...(sel?{borderColor:ev.color,background:"#110f00"}:{}),...(restricted?S.eCardDis:{})}}>
            <div style={{fontSize:28,marginBottom:10,display:"inline-flex",alignItems:"center",justifyContent:"center",width:48,height:48,borderRadius:10,background:ev.color+"18",border:`1px solid ${ev.color}30`}}>{ev.icon}</div>
            <div style={{fontSize:16,fontWeight:600,color:"#E8E0D0",marginBottom:8}}>{key}</div>
            <div style={{display:"flex",flexDirection:"column",gap:4}}>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                <span style={{fontSize:11,color:"#E8E0D0"}}>Base · 2 hrs</span>
                <span style={{fontSize:14,color:"#E8E0D0",fontWeight:700}}>&#8377;{((pricing&&pricing[key])||ev).basePrice.toLocaleString("en-IN")}</span>
              </div>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                <span style={{fontSize:11,color:"#5a5244"}}>Extra per hour</span>
                <span style={{fontSize:13,color:"#c0b8a8"}}>&#8377;{((pricing&&pricing[key])||ev).extraPerHour.toLocaleString("en-IN")}/hr</span>
              </div>
              <div style={{fontSize:11,color:"#9a8e7e",marginTop:2}}>+ 18% GST extra</div>
            </div>
            {restricted&&(
              <div style={{marginTop:8}}>
                <div style={{fontSize:11,color:"#B85450",lineHeight:1.3}}>Not available on weekends 2–10 PM</div>
                <div style={{fontSize:11,color:"#C9A84C",marginTop:3}}>Contact us to book</div>
              </div>
            )}
            {sel&&<div style={{position:"absolute",top:10,right:12,color:ev.color,fontSize:18}}>&#10003;</div>}
          </button>
        );
      })}
    </div>
  );
}

// ── REVIEW COMPONENTS ─────────────────────────────────────────────────────────

function PerformanceSeating({perfTypes,selected,onSelect}) {
  const found=perfTypes.find(p=>p.id===selected);
  return (
    <div style={{marginBottom:24}}>
      <select value={selected||""} onChange={e=>onSelect(e.target.value||null)} style={{...S.inp,marginBottom:12,maxWidth:340}}>
        <option value="">— Select your performance type —</option>
        {perfTypes.map(p=><option key={p.id} value={p.id}>{p.name}</option>)}
      </select>
      {found
        ? <div style={{display:"inline-flex",alignItems:"baseline",gap:8,background:"#141414",border:"1px solid #2a2a2a",borderRadius:8,padding:"14px 20px"}}>
            <span style={{fontSize:13,color:"#6b6356"}}>{found.name}</span>
            <span style={{fontFamily:"'Cormorant Garamond',serif",fontSize:36,fontWeight:700,color:"#C9A84C",lineHeight:1}}>{found.capacity}</span>
            <span style={{fontSize:12,color:"#5a5244"}}>seats</span>
          </div>
        : <div style={{fontSize:13,color:"#3a3530",fontStyle:"italic"}}>Select a type above to see capacity.</div>
      }
    </div>
  );
}

// Shown on payment page for Performance bookings where a sub-type was selected
function PerfSeatingCard({name,capacity}) {
  return (
    <div style={{...S.box,marginTop:0,marginBottom:16,display:"flex",alignItems:"center",justifyContent:"space-between"}}>
      <div>
        <div style={S.lbl}>Performance type &amp; seating</div>
        <div style={{fontSize:14,color:"#E8E0D0",fontWeight:500}}>{name}</div>
      </div>
      <div style={{display:"flex",alignItems:"baseline",gap:6}}>
        <span style={{fontFamily:"'Cormorant Garamond',serif",fontSize:36,fontWeight:700,color:"#C9A84C",lineHeight:1}}>{capacity}</span>
        <span style={{fontSize:12,color:"#5a5244"}}>seats</span>
      </div>
    </div>
  );
}

function TechAvailable({tech}) {
  const sections=Object.entries(tech||{}).filter(([,items])=>items.length>0);
  if(!sections.length) return null;
  const accent={Sound:"#5B9BD5","Light Setup":"#C9A84C",Others:"#6BAE75"};
  return (
    <div style={{marginBottom:24}}>
      <div style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:14}}>
        {sections.map(([sec,items])=>(
          <div key={sec} style={{background:"#141414",border:"1px solid #2a2a2a",borderRadius:8,padding:16}}>
            <div style={{fontSize:11,fontWeight:700,letterSpacing:"0.1em",textTransform:"uppercase",color:accent[sec]||"#C9A84C",marginBottom:12}}>{sec}</div>
            {items.map((item,i)=>(
              <div key={i} style={{fontSize:12,color:"#7a7060",marginBottom:7,display:"flex",gap:7,lineHeight:1.45}}>
                <span style={{color:accent[sec]||"#C9A84C",flexShrink:0,fontSize:10,marginTop:2}}>&#9670;</span>{item}
              </div>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}

function Facilities({eventType,facilities}) {
  const fac=(facilities&&facilities[eventType])||{included:[],excluded:[]};
  const col=(items,color,bg,border,icon,label)=>(
    <div style={{background:bg,border:`1px solid ${border}`,borderRadius:8,padding:16}}>
      <div style={{fontSize:11,fontWeight:700,letterSpacing:"0.1em",textTransform:"uppercase",color,marginBottom:12}}>{icon} {label}</div>
      {items.length===0
        ? <div style={{fontSize:13,color:"#8a9e8b"}}>No items listed</div>
        : items.map(f=><div key={f} style={{fontSize:13,color:color==="#6BAE75"?"#8a9e8b":"#9e8a8a",marginBottom:8,display:"flex",gap:8,lineHeight:1.4}}><span style={{color,flexShrink:0}}>{icon}</span>{f}</div>)
      }
    </div>
  );
  return (
    <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:14,marginBottom:24}}>
      {col(fac.included,"#6BAE75","#0d1a0f","#1e3820","✓","What's included")}
      {col(fac.excluded,"#B85450","#1a0d0d","#3a1e1e","✗","Not included")}
    </div>
  );
}

function PolicySection() {
  const policyItem=(line,color)=>(
    <div style={{display:"flex",gap:12,marginBottom:10,alignItems:"flex-start"}}>
      <span style={{color,fontSize:14,lineHeight:1.6,flexShrink:0}}>&#9670;</span>
      <span style={{fontSize:13,color:"#8a8070",lineHeight:1.7}}>{line}</span>
    </div>
  );
  return (
    <>
      <h3 style={S.h3}>Venue booking policy</h3>
      <div style={{background:"#141414",border:"1px solid #242424",borderRadius:10,padding:"18px 20px",marginBottom:20}}>
        {BOOKING_POLICIES.map(l=>policyItem(l,"#C9A84C"))}
      </div>
      <h3 style={S.h3}>Rescheduling &amp; cancellation policy</h3>
      <div style={{background:"#141414",border:"1px solid #242424",borderRadius:10,padding:"18px 20px",marginBottom:24}}>
        <div style={{fontSize:11,fontWeight:700,letterSpacing:"0.1em",textTransform:"uppercase",color:"#5B9BD5",marginBottom:10}}>Rescheduling / date shift</div>
        {policyItem("Booking dates can be shifted without any additional charges only if the cancelled slot gets rebooked for the same date and time.","#5B9BD5")}
        <div style={{fontSize:11,fontWeight:700,letterSpacing:"0.1em",textTransform:"uppercase",color:"#B85450",marginTop:12,marginBottom:10}}>Cancellation</div>
        {CANCELLATION_POLICIES.map(l=>policyItem(l,"#B85450"))}
      </div>
    </>
  );
}

// ── SUMMARY BOX ───────────────────────────────────────────────────────────────

function SummaryBox({date,start,end,eventType,pricing}) {
  const ev=DEFAULT_EVENTS[eventType], hours=end-start, price=calcPrice(eventType,hours,pricing);
  return (
    <div style={S.box}>
      {[["Date",fmtDate(date)],["Time",`${fmtH(start)} – ${fmtH(end)} (${fmtDuration(hours)})`],["Event type",`${ev.icon} ${eventType}`]].map(([k,v])=>(
        <div key={k} style={S.row}><span style={{color:"#6b6356"}}>{k}</span><span style={{color:"#E8E0D0",fontWeight:500,textAlign:"right"}}>{v}</span></div>
      ))}
      <div style={{borderTop:"1px solid #2a2a2a",margin:"12px 0"}}/>
      {[["Venue charges",price.base],["GST (18%)",price.gst]].map(([k,v])=>(
        <div key={k} style={S.row}><span style={{color:"#6b6356"}}>{k}</span><span style={{color:"#E8E0D0"}}>&#8377;{v.toLocaleString("en-IN")}</span></div>
      ))}
      <div style={{...S.row,marginTop:8}}>
        <span style={{fontSize:16,fontWeight:600,color:"#E8E0D0"}}>Total payable</span>
        <span style={{fontFamily:"'Cormorant Garamond',serif",fontSize:24,color:"#C9A84C",fontWeight:700}}>&#8377;{price.total.toLocaleString("en-IN")}</span>
      </div>
    </div>
  );
}

// ── PAYMENT FORM ──────────────────────────────────────────────────────────────

function PaymentForm({date,start,end,eventType,onConfirm,pricing,contactName,contactEmail,contactPhone,discounts}) {
  const [desc,setDesc]=useState(""), [billName,setBillName]=useState(""), [billAddr,setBillAddr]=useState(""), [gstNo,setGstNo]=useState("");
  const [loading,setLoading]=useState(false), [err,setErr]=useState("");
  const [promoOpen,setPromoOpen]=useState(false), [promoInput,setPromoInput]=useState("");
  const [applied,setApplied]=useState(null), [promoErr,setPromoErr]=useState("");
  const [confirming,setConfirming]=useState(false);
  const price=calcPrice(eventType,end-start,pricing);
  const HR=<div style={{borderTop:"1px solid #2a2a2a",margin:"20px 0"}}/>;

  const discountAmt = applied
    ? applied.type==="fixed"
      ? Math.min(applied.value, price.total)
      : Math.round(price.total * applied.value / 100)
    : 0;
  const finalTotal = price.total - discountAmt;

  const applyPromo = async () => {
    const input = promoInput.trim();
    if(!input) return;
    const hash = await hashCode(input);
    const found = (discounts||[]).find(d=>d.hash===hash&&d.active);
    if(found){ setApplied(found); setPromoErr(""); }
    else { setApplied(null); setPromoErr("Invalid or inactive code."); }
  };

  const pay=async()=>{
    if(!desc.trim()) return setErr("Please provide a brief description of your event");
    if(!billName.trim()||!billAddr.trim()) return setErr("Please fill in all required billing details");

    setErr(""); setLoading(true);

    try {
      // Step 1 — Create Razorpay order on backend
      const orderRes=await fetch("/api/create-order",{
        method:"POST",
        headers:{"Content-Type":"application/json"},
        body:JSON.stringify({amount:finalTotal}),
      });
      const order=await orderRes.json();

      if(!order.id){
        setErr("Could not initiate payment. Please try again.");
        setLoading(false);
        return;
      }

      // Step 2 — Open Razorpay checkout popup
      const options={
        key:process.env.REACT_APP_RAZORPAY_KEY_ID,
        amount:order.amount,
        currency:"INR",
        name:"Artkhoj Performance Space",
        description:`${eventType} — ${date}`,
        order_id:order.id,
        prefill:{name:contactName,email:contactEmail,contact:contactPhone},
        notes:{event_type:eventType,booking_date:date},
        theme:{color:"#C9A84C"},
        handler:async(response)=>{
          // Show processing overlay immediately — prevents user interaction during Firebase save
          setConfirming(true);
          // Step 3 — Verify payment signature on backend
          const verifyRes=await fetch("/api/verify-payment",{
            method:"POST",
            headers:{"Content-Type":"application/json"},
            body:JSON.stringify(response),
          });
          const verification=await verifyRes.json();

          if(!verification.verified){
            setErr("Payment verification failed. Please contact us.");
            setLoading(false);
            return;
          }

          // Step 4 — Payment verified, confirm booking
          await onConfirm({
            id:"VB"+Date.now().toString(36).toUpperCase(),date,start,end,eventType,
            customerName:contactName,customerEmail:contactEmail,customerPhone:contactPhone,
            eventDescription:desc,
            billing:{name:billName,address:billAddr,gstNo:gstNo.trim()||null},
            paymentMethod:"razorpay",
            razorpayPaymentId:response.razorpay_payment_id,
            razorpayOrderId:response.razorpay_order_id,
            base:price.base,gst:price.gst,originalTotal:price.total,
            discountCode:applied?.label||null,discountAmount:discountAmt,
            total:finalTotal,
            bookedAt:new Date().toISOString(),
          });
          setLoading(false);
        },
        modal:{
          ondismiss:()=>{
            setLoading(false);
            setErr("Payment was cancelled. You can try again.");
          },
        },
      };

      const rzp=new window.Razorpay(options);
      rzp.open();

    } catch(error){
      setErr("Something went wrong. Please try again.");
      setLoading(false);
    }
  };

  return (
    <>
    {confirming&&(
      <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.85)",zIndex:999,display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",gap:20}}>
        <div style={{width:48,height:48,border:"3px solid #2a2a2a",borderTop:"3px solid #C9A84C",borderRadius:"50%",animation:"spin 0.9s linear infinite"}}/>
        <div style={{fontFamily:"'Cormorant Garamond',serif",fontSize:24,color:"#E8E0D0",letterSpacing:"0.04em"}}>Confirming your booking…</div>
        <div style={{fontSize:13,color:"#5a5244",textAlign:"center",maxWidth:260,lineHeight:1.7}}>Payment received. Please wait while we confirm your booking.</div>
        <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
      </div>
    )}
    <div style={{...S.box,marginTop:0}}>
      <div style={S.lbl}>Event details</div>
      <div style={{fontSize:12,color:"#5a5244",marginBottom:10,lineHeight:1.6}}>Brief description — type, expected audience size, special requirements, etc.</div>
      <textarea placeholder="e.g. Start and end time of Performance, Classical music recital for ~60 guests, 2 performers on stage, event link..." value={desc} onChange={e=>setDesc(e.target.value)} rows={4} style={{...S.inp,resize:"vertical",lineHeight:1.6,minHeight:96}}/>
      {HR}
      <div style={S.lbl}>Billing details</div>
      <input placeholder="Billing name * (individual or company)" value={billName} onChange={e=>setBillName(e.target.value)} style={S.inp}/>
      <textarea placeholder="Billing address *" value={billAddr} onChange={e=>setBillAddr(e.target.value)} rows={3} style={{...S.inp,resize:"vertical",lineHeight:1.6,minHeight:72}}/>
      <input placeholder="GST number (optional)" value={gstNo} onChange={e=>setGstNo(e.target.value.toUpperCase())} maxLength={15} style={S.inp}/>

      <div style={{marginTop:4,marginBottom:4}}>
        {!promoOpen
          ? <button onClick={()=>setPromoOpen(true)} style={{background:"none",border:"none",color:"#5a5244",fontSize:12,cursor:"pointer",padding:0,fontFamily:"'Outfit',sans-serif",textDecoration:"underline"}}>Have a promo code?</button>
          : <div>
              <div style={{display:"flex",gap:8,marginBottom:4}}>
                <input placeholder="Enter code" value={promoInput} onChange={e=>{setPromoInput(e.target.value.toUpperCase());setPromoErr("");setApplied(null);}} onKeyDown={e=>e.key==="Enter"&&applyPromo()} style={{...S.inp,marginBottom:0,flex:1,fontSize:13}}/>
                <button onClick={applyPromo} style={{...S.btnS,padding:"8px 18px",fontSize:13,whiteSpace:"nowrap"}}>Apply</button>
              </div>
              {promoErr&&<div style={{color:"#B85450",fontSize:12,marginBottom:8}}>{promoErr}</div>}
              {applied&&<div style={{color:"#6BAE75",fontSize:12,marginBottom:8}}>✓ Code applied — {applied.type==="fixed"?`₹${applied.value.toLocaleString("en-IN")} off`:`${applied.value}% off`}</div>}
            </div>
        }
      </div>

      {applied&&(
        <div style={{background:"#0d1a0f",border:"1px solid #1e3820",borderRadius:8,padding:"12px 16px",marginTop:12,marginBottom:4}}>
          <div style={{...S.row,marginBottom:6}}>
            <span style={{color:"#6b6356",fontSize:13}}>Subtotal</span>
            <span style={{color:"#c0b8a8",fontSize:13}}>₹{price.total.toLocaleString("en-IN")}</span>
          </div>
          <div style={{...S.row,marginBottom:0}}>
            <span style={{color:"#6BAE75",fontSize:13}}>Coupon Amount ({applied.label})</span>
            <span style={{color:"#6BAE75",fontSize:13}}>−₹{discountAmt.toLocaleString("en-IN")}</span>
          </div>
          <div style={{borderTop:"1px solid #1e3820",margin:"10px 0"}}/>
          <div style={{...S.row,marginBottom:0}}>
            <span style={{fontSize:15,fontWeight:600,color:"#E8E0D0"}}>Total payable</span>
            <span style={{fontFamily:"'Cormorant Garamond',serif",fontSize:24,color:"#C9A84C",fontWeight:700}}>₹{finalTotal.toLocaleString("en-IN")}</span>
          </div>
        </div>
      )}

      {err&&<div style={{color:"#B85450",fontSize:13,marginTop:12}}>{err}</div>}
      <button onClick={pay} disabled={loading} style={{width:"100%",marginTop:16,padding:"14px 0",background:loading?"#D4A848":"#C9A84C",border:"none",borderRadius:8,color:"#000",fontSize:15,fontWeight:700,cursor:loading?"not-allowed":"pointer",fontFamily:"'Outfit',sans-serif"}}>
        {loading?"Processing payment...":`Pay ₹${finalTotal.toLocaleString("en-IN")}`}
      </button>
    </div>
    </>
  );
}

// ── IMAGE CAROUSEL ────────────────────────────────────────────────────────────

function ImageCarousel({images}) {
  const [idx,setIdx]=useState(0);
  useEffect(()=>{
    if(!images||images.length<=1) return;
    const t=setInterval(()=>setIdx(i=>(i+1)%images.length),4000);
    return ()=>clearInterval(t);
  },[images]);
  if(!images||images.length===0) return (
    <div style={{width:"100%",paddingBottom:"56.25%",position:"relative",borderRadius:10,overflow:"hidden",border:"1px solid #242424",background:"#141414"}}>
      <div style={{position:"absolute",inset:0,display:"flex",alignItems:"center",justifyContent:"center",flexDirection:"column",gap:8}}>
        <span style={{fontSize:32}}>🖼</span>
        <span style={{fontSize:13,color:"#3a3530"}}>No images added yet — add them in Admin → Media</span>
      </div>
    </div>
  );
  const prev=()=>setIdx(i=>(i-1+images.length)%images.length);
  const next=()=>setIdx(i=>(i+1)%images.length);
  return (
    <div style={{position:"relative",width:"100%",paddingBottom:"56.25%",borderRadius:10,overflow:"hidden",border:"1px solid #242424"}}>
      {images.map((img,i)=>(
        <img key={i} src={img.url} alt={img.alt||"Artkhoj venue"} style={{position:"absolute",top:0,left:0,width:"100%",height:"100%",objectFit:"cover",display:"block",opacity:i===idx?1:0,transition:"opacity 0.6s ease"}}/>
      ))}
      {/* Prev / Next */}
      {images.length>1&&<>
        <button onClick={prev} style={{position:"absolute",left:12,top:"50%",transform:"translateY(-50%)",background:"rgba(0,0,0,0.50)",border:"none",color:"#fff",width:36,height:36,borderRadius:"50%",cursor:"pointer",fontSize:18,display:"flex",alignItems:"center",justifyContent:"center",zIndex:2}}>&#8249;</button>
        <button onClick={next} style={{position:"absolute",right:12,top:"50%",transform:"translateY(-50%)",background:"rgba(0,0,0,0.50)",border:"none",color:"#fff",width:36,height:36,borderRadius:"50%",cursor:"pointer",fontSize:18,display:"flex",alignItems:"center",justifyContent:"center",zIndex:2}}>&#8250;</button>
        {/* Dots */}
        <div style={{position:"absolute",bottom:12,left:"50%",transform:"translateX(-50%)",display:"flex",gap:6,zIndex:2}}>
          {images.map((_,i)=><div key={i} onClick={()=>setIdx(i)} style={{width:i===idx?20:8,height:8,borderRadius:4,background:i===idx?"#C9A84C":"rgba(255,255,255,0.4)",cursor:"pointer",transition:"all 0.3s"}}/>)}
        </div>
      </>}
    </div>
  );
}

// ── USER APP ──────────────────────────────────────────────────────────────────

const USER_STEPS=["Date","Time","Event type","Review","Payment"];

function UserApp({bookings,blocked,facilities,capacity,pricing,perfTypes,tech,addBooking,saveLead,heroImage,logoImage,carouselImages,discounts}) {
  const [step,setStep]=useState(0), [date,setDate]=useState(null), [start,setStart]=useState(null);
  const [end,setEnd]=useState(null), [eventType,setEventType]=useState(null);
  const [perfType,setPerfType]=useState(null), [confirmed,setConfirmed]=useState(null);
  const [showDatePicker,setShowDatePicker]=useState(false);
  const [contactName,setContactName]=useState(""), [contactPhone,setContactPhone]=useState(""), [contactEmail,setContactEmail]=useState("");
  const [stepErr,setStepErr]=useState("");
  const confirmedRef=useRef(null);
  useEffect(()=>{ confirmedRef.current=confirmed; },[confirmed]);

  const reset=()=>{
    setStep(0);setDate(null);setStart(null);setEnd(null);setEventType(null);
    setPerfType(null);setConfirmed(null);setShowDatePicker(false);
    setContactName("");setContactPhone("");setContactEmail("");setStepErr("");
    window.history.replaceState({appStep:0},"");
  };

  // Scroll to top on every step change
  useEffect(()=>{ window.scrollTo({top:0,behavior:"smooth"}); },[step]);

  // Push browser history entry when step changes forward
  useEffect(()=>{
    if(step>0 && window.history.state?.appStep!==step){
      window.history.pushState({appStep:step},"");
    }
  },[step]);

  // Handle browser/mobile back button
  useEffect(()=>{
    window.history.replaceState({appStep:0},"");
    const onPop=e=>{
      const s=typeof e.state?.appStep==="number"?e.state.appStep:0;
      // If confirmed booking exists, back always goes to home
       if(confirmedRef.current){ reset(); return; }
      setStep(s);
      if(s<3){ setStepErr(""); }
      if(s<2){ setEventType(null); setPerfType(null); }
      if(s<1){ setStart(null); setEnd(null); setShowDatePicker(false); }
    };
    window.addEventListener("popstate",onPop);
    return ()=>window.removeEventListener("popstate",onPop);
  },[]);

  const handleConfirm=async bk=>{
    const found=perfTypes.find(p=>p.id===perfType);
    const booking={...bk,perfTypeId:perfType||null,perfTypeName:found?.name||null,
      customerName:contactName,customerEmail:contactEmail,customerPhone:contactPhone};
    const saved=await addBooking(booking);
    setConfirmed(saved||booking);
    setStep(5);
  };

  if(step===5&&confirmed) return (
    <div style={{maxWidth:500,margin:"60px auto",padding:"0 24px",textAlign:"center"}}>
      <div style={{width:64,height:64,background:"#6BAE75",borderRadius:"50%",display:"flex",alignItems:"center",justifyContent:"center",fontSize:28,margin:"0 auto 20px",color:"#000"}}>&#10003;</div>
      <h2 style={{fontFamily:"'Cormorant Garamond',serif",fontSize:34,marginBottom:8}}>Booking Confirmed!</h2>
      <div style={{color:"#C9A84C",fontSize:13,fontWeight:700,letterSpacing:"0.12em",marginBottom:8}}>ID: {confirmed.id}</div>
      <p style={{color:"#6b6356",fontSize:14,marginBottom:28}}>Confirmation sent to {confirmed.customerEmail}</p>
      <SummaryBox date={confirmed.date} start={confirmed.start} end={confirmed.end} eventType={confirmed.eventType} pricing={pricing}/>
      <div style={{display:"flex",gap:10,justifyContent:"center",flexWrap:"wrap",marginBottom:20}}>
        <button onClick={reset} style={S.btnP}>Book Another Slot</button>
        <a href={makeGCalUrl(confirmed)} target="_blank" rel="noreferrer" style={{...S.btnS,display:"inline-flex",alignItems:"center",gap:8,textDecoration:"none",padding:"11px 22px"}}>
          <span style={{fontSize:16}}>📅</span> Add to Google Calendar
        </a>
      </div>
      {logoImage&&(
        <div style={{marginTop:28,paddingTop:20,borderTop:"1px solid #1a1a1a",opacity:0.7,display:"flex",justifyContent:"center"}}>
          <img src={logoImage} alt="Artkhoj" style={{maxHeight:48,maxWidth:"40%",objectFit:"contain"}} onError={e=>e.target.style.display="none"}/>
        </div>
      )}
    </div>
  );

  return (
    <div style={{maxWidth:700,margin:"0 auto",padding:"32px 24px 80px"}}>
      {step>0&&<StepBar step={step} steps={USER_STEPS}/>}

      {step===0&&(
        <div>

          {/* About the space — with optional hero image background */}
          <div style={{marginBottom:36,paddingBottom:32,borderBottom:"1px solid #1e1e1e",position:"relative",borderRadius:heroImage?12:0,overflow:heroImage?"hidden":"visible",minHeight:heroImage?220:0}}>
            {heroImage&&<>
              <img src={heroImage} alt="Artkhoj venue" loading="eager" fetchPriority="high" style={{position:"absolute",inset:0,width:"100%",height:"100%",objectFit:"cover",display:"block"}}/>
              <div style={{position:"absolute",inset:0,background:"linear-gradient(to bottom, rgba(0,0,0,0.75) 0%, rgba(0,0,0,0.88) 100%)"}}/>
            </>}
            <div style={{position:"relative",padding:heroImage?"32px 28px":"0"}}>
              {logoImage&&(
                <div style={{marginBottom:20}}>
                  <img src={logoImage} alt="Artkhoj logo" loading="eager" fetchPriority="high" style={{maxHeight:70,maxWidth:"50%",objectFit:"contain"}} onError={e=>e.target.style.display="none"}/>
                </div>
              )}
              <div style={S.eyebrow}>About the space</div>
              <h2 style={{fontFamily:"'Cormorant Garamond',serif",fontSize:28,fontWeight:600,color:"#E8E0D0",lineHeight:1.35,maxWidth:560}}>An intimate creative space where artists, creators, and communities come together to craft memorable experiences.</h2>
            </div>
          </div>

          <LandingActivities/>

          {/* Venue glimpses — above seating capacity */}
          <div style={{marginTop:40}}>
            <div style={S.eyebrow}>Venue glimpses</div>
            <h2 style={S.secHead}>See the space in action</h2>
            <ImageCarousel images={carouselImages}/>
          </div>

          <LandingCapacity capacity={capacity}/>

          {/* Book our venue */}
          <div style={{marginTop:40}}>
            <div style={{marginBottom:24,paddingBottom:20,borderBottom:"1px solid #1e1e1e"}}>
              <div style={S.eyebrow}>Get started</div>
              <h2 style={{fontFamily:"'Cormorant Garamond',serif",fontSize:32,fontWeight:600,color:"#E8E0D0",lineHeight:1.2}}>Book our venue</h2>
            </div>
            <StepBar step={0} steps={USER_STEPS} idle mb={28}/>
            <div style={S.eyebrow}>Book a slot</div>
            <h2 style={{fontFamily:"'Cormorant Garamond',serif",fontSize:28,fontWeight:600,color:"#E8E0D0",marginBottom:20}}>Choose a date</h2>
            <Calendar selected={date} onSelect={d=>{setDate(d);setStart(null);setEnd(null);setEventType(null);}} blocked={blocked}/>
            <div style={{fontSize:13,color:"#5a5244",marginBottom:20,padding:"10px 14px",background:"#141414",border:"1px solid #242424",borderRadius:7,lineHeight:1.7}}>
              ⏰ Online bookings require a minimum of <strong style={{color:"#c0b8a8"}}>48 hours advance notice</strong>. To book for today or tomorrow, please reach out to us directly.
            </div>
            <button disabled={!date} onClick={()=>setStep(1)} style={{...S.btnP,...(!date?S.btnDis:{})}}>Continue &#8594;</button>
          </div>

          {/* Contact */}
          <div style={{marginTop:40,paddingTop:32,borderTop:"1px solid #1e1e1e"}}>
            <div style={S.eyebrow}>Contact us</div>
            <h2 style={{...S.secHead,marginBottom:14}}>Get in touch</h2>
            <p style={{fontSize:14,color:"#6b6356",lineHeight:1.8,marginBottom:20,maxWidth:480}}>In case of any queries or venue visit, please reach out to us on:</p>
            <div style={{display:"flex",flexDirection:"column",gap:12}}>
              {[["mailto:venue.jpn@artkhoj.com","✉","Email","venue.jpn@artkhoj.com"],["tel:+918971068485","📞","Phone","+91 89710 68485"]].map(([href,icon,label,text])=>(
                <a key={href} href={href} style={{display:"inline-flex",alignItems:"center",gap:12,textDecoration:"none",background:"#141414",border:"1px solid #242424",borderRadius:8,padding:"14px 18px",width:"fit-content"}}>
                  <span style={{fontSize:18}}>{icon}</span>
                  <div><div style={{fontSize:10,color:"#5a5244",letterSpacing:"0.1em",textTransform:"uppercase",marginBottom:2}}>{label}</div><div style={{fontSize:14,color:"#C9A84C",fontWeight:500}}>{text}</div></div>
                </a>
              ))}
            </div>
          </div>

          {/* Locate us */}
          <div style={{marginTop:40,paddingTop:32,borderTop:"1px solid #1e1e1e"}}>
            <div style={S.eyebrow}>Locate us</div>
            <h2 style={{...S.secHead,marginBottom:14}}>Find us here</h2>
            <div style={{background:"#141414",border:"1px solid #242424",borderRadius:10,padding:20,maxWidth:480}}>
              <div style={{display:"flex",gap:12,alignItems:"flex-start",marginBottom:16}}>
                <span style={{fontSize:20,marginTop:2}}>📍</span>
                <div>
                  <div style={{fontSize:14,fontWeight:600,color:"#E8E0D0",marginBottom:6}}>Artkhoj — JP Nagar, Bangalore</div>
                  <div style={{fontSize:13,color:"#6b6356",lineHeight:1.8}}>1st Floor, 31/2, 22nd Main Rd,<br/>R.K Colony, Marenahalli, 2nd Phase,<br/>J. P. Nagar, Bengaluru, Karnataka 560078</div>
                </div>
              </div>
              <a href="https://maps.google.com/?q=Artkhoj+JP+Nagar+22nd+Main+Rd+Bengaluru+560078" target="_blank" rel="noreferrer"
                style={{display:"inline-flex",alignItems:"center",gap:8,textDecoration:"none",background:"#0a0a0a",border:"1px solid #2a2a2a",borderRadius:7,padding:"9px 16px",fontSize:13,color:"#5B9BD5",fontFamily:"'Outfit',sans-serif"}}>
                &#127760; Open in Google Maps
              </a>
            </div>
          </div>

          <div style={{height:48}}/>
        </div>
      )}

      {step===1&&(
        <div>
          <h2 style={S.sHead}>Choose your time slot</h2>
          <div style={{display:"flex",alignItems:"center",gap:12,marginBottom:showDatePicker?0:16,flexWrap:"wrap"}}>
            <div style={S.badge}>{fmtDate(date)}</div>
            <button onClick={()=>{setShowDatePicker(!showDatePicker);setStart(null);setEnd(null);}}
              style={{background:"none",border:"none",color:"#5B9BD5",fontSize:13,cursor:"pointer",textDecoration:"underline",fontFamily:"'Outfit',sans-serif",padding:0}}>
              {showDatePicker?"Cancel":"Change date"}
            </button>
          </div>
          {showDatePicker&&(
            <div style={{marginBottom:16}}>
              <Calendar selected={date} onSelect={d=>{setDate(d);setStart(null);setEnd(null);setShowDatePicker(false);}} blocked={blocked}/>
            </div>
          )}
          <TimeSelector key={date} date={date} bookings={bookings} blocked={blocked} onSelect={(s,e)=>{setStart(s);setEnd(e);}}/>
          <div style={{fontSize:12,color:"#5a5244",marginTop:24,marginBottom:10,lineHeight:1.7,padding:"10px 14px",background:"#141414",border:"1px solid #2a2a2a",borderRadius:7}}>
            Can't find your preferred slot? <a href="tel:+918971068485" style={{color:"#C9A84C",textDecoration:"none"}}>Call us</a> or <a href="mailto:venue.jpn@artkhoj.com" style={{color:"#C9A84C",textDecoration:"none"}}>write to us</a> — we may be able to accommodate you.
          </div>
          <div style={{display:"flex",gap:12}}>
            <button onClick={()=>setStep(0)} style={S.btnS}>&#8592; Back</button>
            <button disabled={!start||!end} onClick={()=>setStep(2)} style={{...S.btnP,...(!start||!end?S.btnDis:{})}}>Continue &#8594;</button>
          </div>
        </div>
      )}

      {step===2&&(
        <div>
          <h2 style={S.sHead}>Select event type</h2>
          <div style={S.badge}>{fmtH(start)} – {fmtH(end)} &middot; {end-start} hrs</div>
          <EventCards date={date} start={start} end={end} selected={eventType} onSelect={setEventType} pricing={pricing}/>
          {eventType&&(
            <div style={{marginTop:8,marginBottom:20}}>
              <h3 style={{...S.h3,marginTop:0}}>What&apos;s included</h3>
              <Facilities eventType={eventType} facilities={facilities}/>
            </div>
          )}
          <div style={{fontSize:13,color:"#5a5244",marginBottom:16,padding:"10px 14px",background:"#141414",border:"1px solid #2a2a2a",borderRadius:7,lineHeight:1.7}}>
            &#8377; The total price based on your selected duration and event type will be shown on the next page.
          </div>
          <div style={{display:"flex",gap:12}}>
            <button onClick={()=>setStep(1)} style={S.btnS}>&#8592; Back</button>
            <button disabled={!eventType} onClick={()=>setStep(3)} style={{...S.btnP,...(!eventType?S.btnDis:{})}}>Continue &#8594;</button>
          </div>
        </div>
      )}

      {step===3&&(
        <div>
          <h2 style={S.sHead}>Review your booking</h2>
          <SummaryBox date={date} start={start} end={end} eventType={eventType} pricing={pricing}/>

          {/* Extra hour rate notice */}
          <div style={{fontSize:13,color:"#5a5244",marginBottom:eventType==="Performance"?8:20,padding:"10px 14px",background:"#141414",border:"1px solid #2a2a2a",borderRadius:7,lineHeight:1.7}}>
            &#9432; Every additional hour beyond the booked slot will be charged at{" "}
            <strong style={{color:"#c0b8a8"}}>&#8377;{((pricing&&pricing[eventType])||DEFAULT_EVENTS[eventType])?.extraPerHour?.toLocaleString("en-IN")} + 18% GST</strong> per hour.
          </div>

          {/* Performance-specific setup note */}
          {eventType==="Performance"&&(
            <div style={{fontSize:13,color:"#5a5244",marginBottom:20,padding:"10px 14px",background:"#1a1500",border:"1px solid #2a2420",borderRadius:7,lineHeight:1.7}}>
              &#9888; Please book for the full duration that includes your <strong style={{color:"#c0b8a8"}}>setup, sound &amp; light check, performance, and pack-up</strong> — these are not provided outside your booked slot.
            </div>
          )}

          {/* Contact details — collected here for lead capture */}
          <h3 style={S.h3}>Your details</h3>
          <div style={S.box}>
            {[["Full name *","text",contactName,setContactName],["Phone number *","tel",contactPhone,setContactPhone],["Email address *","email",contactEmail,setContactEmail]].map(([pl,type,val,set])=>(
              <input key={pl} type={type} placeholder={pl} value={val} onChange={e=>{set(e.target.value);setStepErr("");}} style={S.inp}/>
            ))}
          </div>

          {eventType==="Performance"&&<><h3 style={S.h3}>Performance type</h3><div style={{fontSize:13,color:"#C9A84C",marginTop:-10,marginBottom:14,fontStyle:"italic"}}>Capacity figures are without video shoot setup</div><PerformanceSeating perfTypes={perfTypes} selected={perfType} onSelect={p=>{setPerfType(p);setStepErr("");}}/><h3 style={S.h3}>Tech available</h3><TechAvailable tech={tech}/></>}
          {eventType==="Workshop"&&<><h3 style={S.h3}>Seating capacity</h3><div style={{display:"inline-flex",alignItems:"baseline",gap:8,marginBottom:24,background:"#141414",border:"1px solid #2a2a2a",borderRadius:8,padding:"14px 20px"}}><span style={{fontFamily:"'Cormorant Garamond',serif",fontSize:36,fontWeight:700,color:"#5B9BD5",lineHeight:1}}>30</span><span style={{fontSize:12,color:"#5a5244"}}>seats max</span></div></>}

          <PolicySection/>

          {stepErr&&<div style={{fontSize:13,color:"#B85450",padding:"10px 14px",background:"#1a0d0d",border:"1px solid #3a1e1e",borderRadius:6,marginBottom:14}}>{stepErr}</div>}

          <div style={{display:"flex",gap:12}}>
            <button onClick={()=>setStep(2)} style={S.btnS}>&#8592; Back</button>
            <button onClick={()=>{
              if(!contactName.trim()||!contactPhone.trim()||!contactEmail.trim()) return setStepErr("Please fill in your name, phone and email above.");
              if(contactPhone.replace(/\D/g,"").length!==10) return setStepErr("Please enter a valid 10-digit phone number.");
              if(!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(contactEmail.trim())) return setStepErr("Please enter a valid email address.");
              if(eventType==="Performance"&&!perfType) return setStepErr("Please select a performance type to continue.");
              saveLead({id:"L"+Date.now().toString(36).toUpperCase(),capturedAt:new Date().toISOString(),name:contactName,phone:contactPhone,email:contactEmail,date,start,end,eventType,perfTypeId:perfType||null});
              setStepErr(""); setStep(4);
            }} style={S.btnP}>Proceed to Pay &#8594;</button>
          </div>
        </div>
      )}

      {step===4&&(()=>{
        const seating=eventType==="Performance"&&perfType?perfTypes.find(p=>p.id===perfType):null;
        return (
          <div>
            <h2 style={S.sHead}>Complete payment</h2>
            <SummaryBox date={date} start={start} end={end} eventType={eventType} pricing={pricing}/>
            {seating&&<PerfSeatingCard name={seating.name} capacity={seating.capacity}/>}
            <h3 style={{fontFamily:"'Cormorant Garamond',serif",fontSize:20,margin:"20px 0 14px",color:"#E8E0D0"}}>Payment details</h3>
            <PaymentForm date={date} start={start} end={end} eventType={eventType} onConfirm={handleConfirm} pricing={pricing} contactName={contactName} contactEmail={contactEmail} contactPhone={contactPhone} discounts={discounts}/>
            <button onClick={()=>setStep(3)} style={{...S.btnS,marginTop:12}}>&#8592; Back</button>
          </div>
        );
      })()}
    </div>
  );
}

// ── ADMIN: SHARED ROW-LIST EDITOR ─────────────────────────────────────────────
// Replaces CapacityEditor and PerfTypesEditor — both had identical structure.
// nameField: the object key for the row's label ('format' or 'name').

function RowListEditor({title,storageKey,defaultData,nameField,rows:initRows,onChange}) {
  const [rows,setRows]=useState(initRows), [newName,setNewName]=useState(""), [newCap,setNewCap]=useState("");
  const [editId,setEditId]=useState(null), [flash,setFlash]=useState(false);
  useEffect(()=>setRows(initRows),[initRows]);

  const persist=async upd=>{setRows(upd);onChange(upd);await saveData(storageKey,upd);setFlash(true);setTimeout(()=>setFlash(false),1800);};
  const addRow =()=>{ const n=newName.trim(),c=parseInt(newCap,10); if(!n||!c||c<1)return; persist([...rows,{id:storageKey+Date.now(),[nameField]:n,capacity:c}]); setNewName("");setNewCap(""); };
  const remove =id=>persist(rows.filter(r=>r.id!==id));
  const move   =(idx,dir)=>{ const a=[...rows],to=idx+dir; if(to<0||to>=a.length)return; [a[idx],a[to]]=[a[to],a[idx]]; persist(a); };
  const commit =(id,name,cap)=>{ const c=parseInt(cap,10); if(!name.trim()||!c||c<1){setEditId(null);return;} persist(rows.map(r=>r.id===id?{...r,[nameField]:name.trim(),capacity:c}:r)); setEditId(null); };

  const arrowPair=(idx)=>(
    <div style={{display:"flex",flexDirection:"column"}}>
      <button onClick={()=>move(idx,-1)} disabled={idx===0} style={S.arrBtn(idx===0)}>&#9650;</button>
      <button onClick={()=>move(idx,1)}  disabled={idx===rows.length-1} style={S.arrBtn(idx===rows.length-1)}>&#9660;</button>
    </div>
  );

  return (
    <div style={{background:"#141414",border:"1px solid #2a2a2a",borderRadius:10,overflow:"hidden",marginBottom:28}}>
      <div style={{padding:"11px 16px",borderBottom:"1px solid #2a2a2a",display:"flex",alignItems:"center",justifyContent:"space-between"}}>
        <div style={{display:"flex",gap:10,alignItems:"center"}}>
          <span style={{fontSize:11,fontWeight:700,letterSpacing:"0.1em",textTransform:"uppercase",color:"#C9A84C"}}>{title}</span>
          {flash&&<span style={{fontSize:11,color:"#6BAE75"}}>&#10003; Saved</span>}
        </div>
        <button onClick={()=>persist(defaultData.map(r=>({...r})))} style={{background:"none",border:"1px solid #2a2a2a",color:"#5a5244",padding:"5px 12px",borderRadius:6,cursor:"pointer",fontSize:11,fontFamily:"'Outfit',sans-serif"}}>&#8635; Reset</button>
      </div>
      {rows.map((row,idx)=>{
        if(editId===row.id) return (
          <RowEdit key={row.id} vals={[row[nameField],String(row.capacity)]} onDone={v=>commit(row.id,v[0],v[1])} onCancel={()=>setEditId(null)} idx={idx} total={rows.length} onMove={d=>move(idx,d)} wide={[3,1]}/>
        );
        return (
          <div key={row.id} style={{display:"flex",alignItems:"center",gap:8,padding:"8px 14px",borderBottom:"1px solid #1e1e1e"}}>
            {arrowPair(idx)}
            <span style={{flex:3,fontSize:13,color:"#c0b8a8"}}>{row[nameField]}</span>
            <span style={{textAlign:"center",fontFamily:"'Cormorant Garamond',serif",fontSize:20,color:"#C9A84C",fontWeight:600,width:60}}>{row.capacity}</span>
            <span style={{fontSize:11,color:"#5a5244",width:24}}>pax</span>
            <button onClick={()=>setEditId(row.id)} style={{background:"none",border:"1px solid #2a2a2a",color:"#5a5244",padding:"4px 10px",borderRadius:5,cursor:"pointer",fontSize:12,fontFamily:"'Outfit',sans-serif"}}>Edit</button>
            <button onClick={()=>remove(row.id)} style={{background:"none",border:"1px solid #2a2a2a",color:"#5a5244",width:22,height:22,borderRadius:4,cursor:"pointer",fontSize:14,display:"flex",alignItems:"center",justifyContent:"center"}}>&#215;</button>
          </div>
        );
      })}
      <div style={{padding:"10px 12px",borderTop:"1px solid #2a2a2a",display:"flex",gap:8}}>
        <input value={newName} onChange={e=>setNewName(e.target.value)} onKeyDown={e=>e.key==="Enter"&&addRow()} placeholder="Name" style={{...S.inp,flex:3,marginBottom:0,fontSize:12,padding:"7px 10px"}}/>
        <input value={newCap} onChange={e=>setNewCap(e.target.value.replace(/\D/g,""))} onKeyDown={e=>e.key==="Enter"&&addRow()} placeholder="Cap." style={{...S.inp,width:64,marginBottom:0,fontSize:12,padding:"7px 8px",textAlign:"center"}}/>
        <button onClick={addRow} style={{background:"#C9A84C22",border:"1px solid #C9A84C55",color:"#C9A84C",padding:"7px 14px",borderRadius:6,cursor:"pointer",fontSize:18,fontFamily:"'Outfit',sans-serif",flexShrink:0,lineHeight:1}}>+</button>
      </div>
    </div>
  );
}

// Inline edit row used by RowListEditor
function RowEdit({vals,onDone,onCancel,idx,total,onMove,wide}) {
  const [v,setV]=useState([...vals]);
  return (
    <div style={{display:"flex",alignItems:"center",gap:8,padding:"7px 14px",borderBottom:"1px solid #1e1e1e",background:"#1a1a0a"}}>
      <div style={{display:"flex",flexDirection:"column"}}>
        <button onClick={()=>onMove(-1)} disabled={idx===0} style={S.arrBtn(idx===0)}>&#9650;</button>
        <button onClick={()=>onMove(1)}  disabled={idx===total-1} style={S.arrBtn(idx===total-1)}>&#9660;</button>
      </div>
      {v.map((val,i)=><input key={i} value={val} onChange={e=>{const n=[...v];n[i]=e.target.value.replace(i>0?/\D/g:"","")||val;setV(n);}} autoFocus={i===0} style={{...S.inp,flex:wide?wide[i]:1,marginBottom:0,fontSize:12,padding:"6px 8px",...(i>0?{textAlign:"center"}:{})}}/>)}
      <button onClick={()=>onDone(v)} style={{background:"#6BAE7522",border:"1px solid #6BAE7555",color:"#6BAE75",padding:"6px 10px",borderRadius:5,cursor:"pointer",fontSize:13,fontFamily:"'Outfit',sans-serif"}}>&#10003;</button>
      <button onClick={onCancel}     style={{background:"none",border:"1px solid #2a2a2a",color:"#5a5244",padding:"6px 10px",borderRadius:5,cursor:"pointer",fontSize:13,fontFamily:"'Outfit',sans-serif"}}>&#10007;</button>
    </div>
  );
}

// ── ADMIN: TECH EDITOR ────────────────────────────────────────────────────────

function TechEditor({tech,onChange}) {
  const sections=Object.keys(DEFAULT_TECH);
  const [newItems,setNewItems]=useState(()=>Object.fromEntries(sections.map(s=>[s,""])));
  const [flash,setFlash]=useState(false);
  const accent={Sound:"#5B9BD5","Light Setup":"#C9A84C",Others:"#6BAE75"};

  const persist=async upd=>{onChange(upd);await saveData("vb_tech",upd);setFlash(true);setTimeout(()=>setFlash(false),1800);};
  const add  =(sec,val)=>{ const v=val.trim(); if(!v||(tech[sec]||[]).includes(v))return; persist({...tech,[sec]:[...(tech[sec]||[]),v]}); setNewItems(n=>({...n,[sec]:""})); };
  const remove=(sec,i) =>persist({...tech,[sec]:(tech[sec]||[]).filter((_,j)=>j!==i)});
  const move  =(sec,i,dir)=>{ const a=[...(tech[sec]||[])],to=i+dir; if(to<0||to>=a.length)return; [a[i],a[to]]=[a[to],a[i]]; persist({...tech,[sec]:a}); };

  return (
    <div style={{marginBottom:28}}>
      <div style={{display:"flex",justifyContent:"flex-end",alignItems:"center",gap:10,marginBottom:10}}>
        {flash&&<span style={{fontSize:11,color:"#6BAE75"}}>&#10003; Saved</span>}
        <button onClick={()=>persist(Object.fromEntries(Object.entries(DEFAULT_TECH).map(([k,v])=>[k,[...v]])))} style={{background:"none",border:"1px solid #2a2a2a",color:"#5a5244",padding:"6px 14px",borderRadius:6,cursor:"pointer",fontSize:11,fontFamily:"'Outfit',sans-serif"}}>&#8635; Reset to defaults</button>
      </div>
      <div style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:14}}>
        {sections.map(sec=>{
          const items=tech[sec]||[], ac=accent[sec]||"#C9A84C";
          return (
            <div key={sec} style={{background:"#141414",border:"1px solid #2a2a2a",borderRadius:10,overflow:"hidden"}}>
              <div style={{padding:"11px 14px",borderBottom:"1px solid #2a2a2a",display:"flex",justifyContent:"space-between"}}>
                <span style={{fontSize:11,fontWeight:700,letterSpacing:"0.1em",textTransform:"uppercase",color:ac}}>{sec}</span>
                <span style={{fontSize:11,color:"#3a3530"}}>{items.length}</span>
              </div>
              <div style={{minHeight:80}}>
                {items.length===0&&<div style={{fontSize:12,color:"#3a3530",textAlign:"center",padding:"18px 0"}}>No items</div>}
                {items.map((item,i)=>(
                  <div key={i} style={{display:"flex",alignItems:"flex-start",gap:6,padding:"6px 10px",borderBottom:"1px solid #1a1a1a"}}>
                    <div style={{display:"flex",flexDirection:"column",flexShrink:0}}>
                      <button onClick={()=>move(sec,i,-1)} disabled={i===0} style={S.arrBtn(i===0)}>&#9650;</button>
                      <button onClick={()=>move(sec,i,1)}  disabled={i===items.length-1} style={S.arrBtn(i===items.length-1)}>&#9660;</button>
                    </div>
                    <span style={{flex:1,fontSize:12,color:"#c0b8a8",lineHeight:1.4}}>{item}</span>
                    <button onClick={()=>remove(sec,i)} style={{background:"none",border:"none",color:"#5a5244",cursor:"pointer",fontSize:14,flexShrink:0,lineHeight:1}}>&#215;</button>
                  </div>
                ))}
              </div>
              <div style={{padding:"8px 10px",borderTop:"1px solid #2a2a2a",display:"flex",gap:6}}>
                <input value={newItems[sec]} onChange={e=>setNewItems(n=>({...n,[sec]:e.target.value}))} onKeyDown={e=>e.key==="Enter"&&add(sec,newItems[sec])} placeholder="Add item..." style={{...S.inp,flex:1,marginBottom:0,fontSize:11,padding:"6px 8px"}}/>
                <button onClick={()=>add(sec,newItems[sec])} style={{background:ac+"22",border:`1px solid ${ac}55`,color:ac,padding:"6px 12px",borderRadius:5,cursor:"pointer",fontSize:16,fontFamily:"'Outfit',sans-serif",flexShrink:0,lineHeight:1}}>+</button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── ADMIN: FACILITIES EDITOR ──────────────────────────────────────────────────

function FacilitiesEditor({facilities,onChange}) {
  const types=Object.keys(DEFAULT_EVENTS);
  const [active,setActive]=useState(types[0]), [newInc,setNewInc]=useState(""), [newExc,setNewExc]=useState(""), [flash,setFlash]=useState(false);
  const fac=facilities[active]||{included:[],excluded:[]};
  const persist  =async upd=>{onChange(upd);await saveData("vb_fac",upd);setFlash(true);setTimeout(()=>setFlash(false),1800);};
  const updateCol=(col,items)=>persist({...facilities,[active]:{...fac,[col]:items}});
  const addItem  =(col,val,setter)=>{ const v=val.trim(); if(!v||fac[col].includes(v))return; updateCol(col,[...fac[col],v]); setter(""); };
  const removeItem=(col,item)=>updateCol(col,fac[col].filter(x=>x!==item));
  const moveItem  =(col,idx,dir)=>{ const a=[...fac[col]],to=idx+dir; if(to<0||to>=a.length)return; [a[idx],a[to]]=[a[to],a[idx]]; updateCol(col,a); };
  const reset=()=>{ const def=DEFAULT_EVENTS[active]; persist({...facilities,[active]:{included:[...def.included],excluded:[...def.excluded]}}); };
  const ev=DEFAULT_EVENTS[active];
  const cols=[{col:"included",label:"✓ Included",accent:"#6BAE75",bg:"#0d1a0f",border:"#1e3820",val:newInc,set:setNewInc},{col:"excluded",label:"✗ Not Included",accent:"#B85450",bg:"#1a0d0d",border:"#3a1e1e",val:newExc,set:setNewExc}];

  return (
    <div>
      <div style={{display:"flex",gap:6,marginBottom:16,flexWrap:"wrap",alignItems:"center"}}>
        {types.map(type=>{ const e=DEFAULT_EVENTS[type],act=active===type; return(
          <button key={type} onClick={()=>{setActive(type);setNewInc("");setNewExc("");}} style={{padding:"8px 16px",borderRadius:7,border:`1px solid ${act?e.color+"80":"#2a2a2a"}`,background:act?e.color+"18":"none",color:act?e.color:"#6b6356",cursor:"pointer",fontSize:13,fontFamily:"'Outfit',sans-serif",display:"flex",alignItems:"center",gap:6}}>
            <span>{e.icon}</span>{type}
          </button>
        );})}
        {flash&&<span style={{fontSize:12,color:"#6BAE75",marginLeft:6}}>&#10003; Saved</span>}
      </div>
      <div style={{fontSize:13,color:"#5a5244",marginBottom:14,padding:"10px 14px",background:"#141414",borderRadius:7,border:"1px solid #2a2a2a"}}>
        Editing <span style={{color:ev.color,fontWeight:600}}>{ev.icon} {active}</span>. Changes save instantly.
      </div>
      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:16,marginBottom:12}}>
        {cols.map(({col,label,accent,bg,border,val,set})=>{
          const items=fac[col]||[];
          return (
            <div key={col} style={{background:bg,border:`1px solid ${border}`,borderRadius:10,overflow:"hidden",display:"flex",flexDirection:"column"}}>
              <div style={{padding:"11px 14px",borderBottom:`1px solid ${border}`,display:"flex",justifyContent:"space-between"}}>
                <span style={{fontSize:11,fontWeight:700,letterSpacing:"0.1em",textTransform:"uppercase",color:accent}}>{label}</span>
                <span style={{fontSize:11,color:"#3a3530"}}>{items.length}</span>
              </div>
              <div style={{flex:1,minHeight:100}}>
                {items.length===0?<div style={{fontSize:13,color:"#3a3530",textAlign:"center",padding:"24px 12px"}}>No items yet</div>:items.map((item,idx)=>(
                  <div key={item+idx} style={{display:"flex",alignItems:"center",gap:6,padding:"6px 10px",borderBottom:`1px solid ${border}40`}}>
                    <div style={{display:"flex",flexDirection:"column",flexShrink:0}}>
                      <button onClick={()=>moveItem(col,idx,-1)} disabled={idx===0} style={S.arrBtn(idx===0)}>&#9650;</button>
                      <button onClick={()=>moveItem(col,idx,1)}  disabled={idx===items.length-1} style={S.arrBtn(idx===items.length-1)}>&#9660;</button>
                    </div>
                    <span style={{flex:1,fontSize:13,color:"#c0b8a8",lineHeight:1.4,wordBreak:"break-word"}}>{item}</span>
                    <button onClick={()=>removeItem(col,item)} style={{background:"none",border:`1px solid ${border}80`,color:"#5a5244",width:22,height:22,borderRadius:4,cursor:"pointer",fontSize:14,display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>&#215;</button>
                  </div>
                ))}
              </div>
              <div style={{padding:"10px",borderTop:`1px solid ${border}`,display:"flex",gap:6}}>
                <input value={val} onChange={e=>set(e.target.value)} onKeyDown={e=>e.key==="Enter"&&addItem(col,val,set)} placeholder={`Add ${col==="included"?"included":"excluded"} item...`} style={{...S.inp,flex:1,marginBottom:0,fontSize:12,padding:"7px 10px",background:"#0a0a0a",border:`1px solid ${border}`}}/>
                <button onClick={()=>addItem(col,val,set)} style={{background:accent+"22",border:`1px solid ${accent}55`,color:accent,padding:"7px 14px",borderRadius:6,cursor:"pointer",fontSize:18,fontFamily:"'Outfit',sans-serif",flexShrink:0,lineHeight:1}}>+</button>
              </div>
            </div>
          );
        })}
      </div>
      <div style={{display:"flex",justifyContent:"flex-end"}}>
        <button onClick={reset} style={{background:"none",border:"1px solid #2a2a2a",color:"#5a5244",padding:"7px 16px",borderRadius:6,cursor:"pointer",fontSize:12,fontFamily:"'Outfit',sans-serif"}}>&#8635; Reset {active} to defaults</button>
      </div>
    </div>
  );
}

// ── ADMIN: PRICING EDITOR ─────────────────────────────────────────────────────

function PricingEditor({pricing,onChange}) {
  const types=Object.keys(DEFAULT_EVENTS);
  const [draft,setDraft]=useState(()=>Object.fromEntries(types.map(k=>[k,{basePrice:String((pricing[k]||DEFAULT_EVENTS[k]).basePrice),extraPerHour:String((pricing[k]||DEFAULT_EVENTS[k]).extraPerHour)}])));
  const [flash,setFlash]=useState(false), [errors,setErrors]=useState({});

  const setField=(type,field,val)=>{ setDraft(d=>({...d,[type]:{...d[type],[field]:val.replace(/\D/g,"")}})); setErrors(e=>({...e,[type+field]:false})); };
  const save=async()=>{
    const errs={}; types.forEach(k=>{ if(!+draft[k].basePrice)errs[k+"bp"]=true; if(!+draft[k].extraPerHour)errs[k+"ep"]=true; });
    if(Object.keys(errs).length){setErrors(errs);return;}
    const upd=Object.fromEntries(types.map(k=>[k,{basePrice:+draft[k].basePrice,extraPerHour:+draft[k].extraPerHour}]));
    onChange(upd); await saveData("vb_pricing",upd); setFlash(true); setTimeout(()=>setFlash(false),2000);
  };
  const reset=()=>{ setDraft(Object.fromEntries(types.map(k=>[k,{basePrice:String(DEFAULT_EVENTS[k].basePrice),extraPerHour:String(DEFAULT_EVENTS[k].extraPerHour)}]))); setErrors({}); };
  const fmt=n=>Number(n).toLocaleString("en-IN");

  return (
    <div>
      <div style={{display:"grid",gridTemplateColumns:"1fr 160px 160px 1fr",gap:12,padding:"8px 16px",marginBottom:4}}>
        {["Event type","Base rate (2 hrs)","Extra per hour","Preview (incl. GST)"].map(h=><div key={h} style={{fontSize:11,color:"#3a3530",letterSpacing:"0.08em",textTransform:"uppercase",textAlign:h==="Event type"?"left":"center"}}>{h}</div>)}
      </div>
      {types.map((key,i)=>{
        const ev=DEFAULT_EVENTS[key], bp=+draft[key].basePrice||0, ep=+draft[key].extraPerHour||0;
        const gst=Math.round(bp*ev.gst), tot2=bp+gst, tot3=bp+ep+Math.round((bp+ep)*ev.gst);
        return (
          <div key={key} style={{display:"grid",gridTemplateColumns:"1fr 160px 160px 1fr",gap:12,alignItems:"center",background:i%2===0?"#141414":"#111",borderRadius:8,padding:"14px 16px",marginBottom:6}}>
            <div style={{display:"flex",alignItems:"center",gap:10}}>
              <span style={{fontSize:22}}>{ev.icon}</span>
              <div><div style={{fontSize:14,fontWeight:600,color:ev.color}}>{key}</div><div style={{fontSize:11,color:"#3a3530"}}>+ 18% GST</div></div>
            </div>
            {[["basePrice","bp"],["extraPerHour","ep"]].map(([field,errKey])=>(
              <div key={field} style={{position:"relative"}}>
                <span style={{position:"absolute",left:10,top:"50%",transform:"translateY(-50%)",fontSize:13,color:"#5a5244",pointerEvents:"none"}}>&#8377;</span>
                <input value={draft[key][field]} onChange={e=>setField(key,field,e.target.value)} style={{...S.inp,marginBottom:0,paddingLeft:24,textAlign:"right",border:`1px solid ${errors[key+errKey]?"#B85450":"#2a2a2a"}`,borderRadius:6}}/>
                {errors[key+errKey]&&<div style={{fontSize:10,color:"#B85450",marginTop:2}}>Required</div>}
              </div>
            ))}
            <div style={{fontSize:12,color:"#6b6356",lineHeight:1.8}}>
              <div>2 hrs — <span style={{color:"#C9A84C",fontWeight:600}}>&#8377;{fmt(tot2)}</span></div>
              <div>3 hrs — <span style={{color:"#C9A84C",fontWeight:600}}>&#8377;{fmt(tot3)}</span></div>
              <div style={{fontSize:11,color:"#3a3530"}}>Extra hr: +&#8377;{fmt(ep)} (+ GST)</div>
            </div>
          </div>
        );
      })}
      <div style={{display:"flex",alignItems:"center",justifyContent:"flex-end",gap:10,marginTop:16}}>
        {flash&&<span style={{fontSize:12,color:"#6BAE75"}}>&#10003; Prices saved</span>}
        <button onClick={reset} style={{background:"none",border:"1px solid #2a2a2a",color:"#5a5244",padding:"8px 16px",borderRadius:6,cursor:"pointer",fontSize:12,fontFamily:"'Outfit',sans-serif"}}>&#8635; Reset to defaults</button>
        <button onClick={save} style={{background:"#C9A84C",border:"none",color:"#000",padding:"9px 22px",borderRadius:6,cursor:"pointer",fontSize:13,fontWeight:700,fontFamily:"'Outfit',sans-serif"}}>Save prices</button>
      </div>
    </div>
  );
}

// ── ADMIN: BOOKING EDITOR ─────────────────────────────────────────────────────

function BookingEditor({booking,allBookings,onSave,onCancel}) {
  const [date,setDate]=useState(booking.date), [start,setStart]=useState(booking.start), [end,setEnd]=useState(booking.end), [err,setErr]=useState("");
  const others=allBookings.filter(b=>b.id!==booking.id&&b.date===booking.date&&!b.cancelled);
  const hasConflict=(d,s,e)=>others.some(o=>o.date===d&&s<o.end+1&&e>o.start-1);
  const save=()=>{
    if(!date||start===null||end===null) return setErr("Please fill in all fields.");
    if(end<=start+1) return setErr("Minimum booking is 2 hours.");
    if(hasConflict(date,start,end)) return setErr("This slot (including 1-hr buffer) conflicts with another booking.");
    onSave({...booking,date,start,end});
  };
  return (
    <div style={{background:"#1a1500",border:"1px solid #C9A84C44",borderRadius:10,padding:20,marginTop:10}}>
      <div style={{fontSize:12,color:"#C9A84C",fontWeight:700,letterSpacing:"0.08em",textTransform:"uppercase",marginBottom:14}}>Reschedule booking · {booking.id}</div>
      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:12,marginBottom:12}}>
        <div><div style={{fontSize:11,color:"#5a5244",marginBottom:6}}>New date</div><input type="date" value={date} onChange={e=>{setDate(e.target.value);setStart(null);setEnd(null);}} min={todayStr()} style={{...S.inp,marginBottom:0}}/></div>
        <div><div style={{fontSize:11,color:"#5a5244",marginBottom:6}}>Start time</div><select value={start??""} onChange={e=>{setStart(+e.target.value);setEnd(null);}} style={{...S.inp,marginBottom:0}}><option value="">Select</option>{HALF_HOURS_START.map(h=><option key={h} value={h}>{fmtH(h)}</option>)}</select></div>
        <div><div style={{fontSize:11,color:"#5a5244",marginBottom:6}}>End time</div><select value={end??""} onChange={e=>setEnd(+e.target.value)} style={{...S.inp,marginBottom:0}}><option value="">Select</option>{start!==null&&Array.from({length:Math.floor(21-start)+1},(_,i)=>+(start+2+i).toFixed(1)).filter(h=>h<=23).map(h=><option key={h} value={h}>{fmtH(h)}</option>)}</select></div>
      </div>
      {err&&<div style={{color:"#B85450",fontSize:12,marginBottom:10}}>{err}</div>}
      <div style={{display:"flex",gap:8}}>
        <button onClick={save} style={{...S.btnP,padding:"8px 20px",fontSize:13}}>Save changes</button>
        <button onClick={onCancel} style={{...S.btnS,padding:"8px 20px",fontSize:13}}>Cancel</button>
      </div>
    </div>
  );
}

// ── ADMIN: GCAL SETTINGS ──────────────────────────────────────────────────────

const APPS_SCRIPT_CODE=`function doPost(e) {
  try {
    var data = JSON.parse(e.postData.contents);
    var cal = data.calendarId
      ? CalendarApp.getCalendarById(data.calendarId)
      : CalendarApp.getDefaultCalendar();
    cal.createEvent(data.title, new Date(data.startISO), new Date(data.endISO), {
      description: data.description
    });
    return ContentService
      .createTextOutput(JSON.stringify({ ok: true }))
      .setMimeType(ContentService.MimeType.JSON);
  } catch(err) {
    return ContentService
      .createTextOutput(JSON.stringify({ ok: false, error: err.message }))
      .setMimeType(ContentService.MimeType.JSON);
  }
}`;

function GCalSettings({gcalConfig,onSave}) {
  const [webhookUrl,setWebhookUrl]=useState(gcalConfig?.webhookUrl||"");
  const [calendarId,setCalendarId]=useState(gcalConfig?.calendarId||"");
  const [adminEmail,setAdminEmail]=useState(gcalConfig?.adminEmail||"");
  const [flash,setFlash]=useState(null), [copied,setCopied]=useState(false);

  const save=async()=>{ await onSave({webhookUrl:webhookUrl.trim(),calendarId:calendarId.trim(),adminEmail:adminEmail.trim()}); setFlash({ok:true,text:"Saved."}); setTimeout(()=>setFlash(null),2000); };

  const test=async()=>{
    const url=webhookUrl.trim();
    if(!url) return setFlash({ok:false,text:"Enter a webhook URL first."});
    if(!url.startsWith("https://script.google.com/macros/s/")){ setFlash({ok:false,text:"URL should start with https://script.google.com/macros/s/"}); setTimeout(()=>setFlash(null),6000); return; }
    if(url.endsWith("/dev")){ setFlash({ok:false,text:"You have a /dev URL. Go to Deploy → New deployment → Web app, set Who has access: Anyone, and use the /exec URL."}); setTimeout(()=>setFlash(null),10000); return; }
    if(!url.endsWith("/exec")){ setFlash({ok:false,text:"URL should end with /exec — copy the full deployment URL."}); setTimeout(()=>setFlash(null),6000); return; }
    try {
      await fetch(url,{method:"POST",mode:"no-cors",body:JSON.stringify({action:"booking",title:"[Test] Artkhoj",date:"2024-01-01",startHour:10,endHour:12,startISO:"2024-01-01T10:00:00+05:30",endISO:"2024-01-01T12:00:00+05:30",calendarId:calendarId.trim(),adminEmail:adminEmail.trim(),customerEmail:"",bookingId:"TEST",eventType:"Test",customerName:"Test User",customerPhone:"-",dateFormatted:"1 Jan 2025",timeFormatted:"10 AM – 12 PM",totalAmount:"0",paymentMethod:"test",description:"Test event."})});
      setFlash({ok:true,text:"Request sent — check your Google Calendar."});
    } catch(e) {
      setFlash({ok:true,text:"URL looks valid ✓  Auto-sync works in a real browser (this sandbox blocks external requests)."});
    }
    setTimeout(()=>setFlash(null),8000);
  };

  return (
    <div style={{maxWidth:640}}>
      <div style={{background:"#141414",border:"1px solid #2a2a2a",borderRadius:10,padding:24}}>
        <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:4}}><span style={{fontSize:18}}>📅</span><span style={{fontSize:13,fontWeight:700,color:"#E8E0D0"}}>Google Calendar sync</span></div>
        <div style={{fontSize:12,color:"#5a5244",marginBottom:20,lineHeight:1.7}}>Automatically create a Google Calendar event and send confirmation emails for every confirmed booking. Requires a one-time Apps Script setup.</div>

        {[["Apps Script webhook URL","https://script.google.com/macros/s/AKfy.../exec",webhookUrl,setWebhookUrl,"text"],
          ["Calendar ID (optional — blank = default calendar)","your@gmail.com or abc@group.calendar.google.com",calendarId,setCalendarId,"text"],
          ["Admin notification email","venue.jpn@artkhoj.com",adminEmail,setAdminEmail,"email"]
        ].map(([label,placeholder,val,set,type])=>(
          <div key={label}>
            <div style={{fontSize:11,color:"#5a5244",marginBottom:6,letterSpacing:"0.06em",textTransform:"uppercase"}}>{label}</div>
            <input type={type} placeholder={placeholder} value={val} onChange={e=>set(e.target.value)} style={S.inp}/>
          </div>
        ))}

        {flash&&<div style={{fontSize:13,color:flash.ok?"#6BAE75":"#B85450",marginBottom:12,display:"flex",alignItems:"center",gap:6}}><span>{flash.ok?"✓":"✗"}</span>{flash.text}</div>}

        <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
          <button onClick={save} style={{...S.btnP,padding:"9px 22px",fontSize:13}}>Save</button>
          <button onClick={test} style={{...S.btnS,padding:"9px 22px",fontSize:13}}>Send test event</button>
          {webhookUrl&&<button onClick={()=>{setWebhookUrl("");setCalendarId("");setAdminEmail("");onSave({webhookUrl:"",calendarId:"",adminEmail:""}); }} style={{background:"none",border:"1px solid #3a2a2a",color:"#5a3a3a",padding:"9px 16px",borderRadius:7,cursor:"pointer",fontSize:13,fontFamily:"'Outfit',sans-serif"}}>Disconnect</button>}
        </div>

        <div style={{marginTop:24,borderTop:"1px solid #2a2a2a",paddingTop:20}}>
          <div style={{fontSize:12,fontWeight:600,color:"#9a8e7e",marginBottom:10}}>How to set up (5 minutes)</div>
          {["Go to script.google.com and click New project.","Delete the placeholder code and paste the script below.","Click Deploy → New deployment → Web app.","Set Execute as: Me and Who has access: Anyone. Click Deploy.","Copy the /exec URL and paste it above.","(Optional) Add a Calendar ID and admin email above.","Note: test button validates the URL. Auto-sync works in a real browser — this preview sandbox blocks outbound requests."].map((s,i)=>(
            <div key={i} style={{display:"flex",gap:10,marginBottom:8,alignItems:"flex-start"}}>
              <span style={{background:"#2a2a2a",color:"#C9A84C",borderRadius:"50%",width:20,height:20,display:"flex",alignItems:"center",justifyContent:"center",fontSize:10,fontWeight:700,flexShrink:0,marginTop:1}}>{i+1}</span>
              <span style={{fontSize:12,color:"#6b6356",lineHeight:1.6}}>{s}</span>
            </div>
          ))}
          <div style={{marginTop:14}}>
            <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:8}}>
              <span style={{fontSize:11,color:"#5a5244",letterSpacing:"0.06em",textTransform:"uppercase"}}>Apps Script code</span>
              <button onClick={()=>navigator.clipboard.writeText(APPS_SCRIPT_CODE).then(()=>{setCopied(true);setTimeout(()=>setCopied(false),2000);})} style={{background:copied?"#6BAE7522":"#1a1a1a",border:`1px solid ${copied?"#6BAE75":"#2a2a2a"}`,color:copied?"#6BAE75":"#5a5244",padding:"4px 12px",borderRadius:5,cursor:"pointer",fontSize:11,fontFamily:"'Outfit',sans-serif"}}>
                {copied?"✓ Copied":"Copy"}
              </button>
            </div>
            <pre style={{background:"#0a0a0a",border:"1px solid #2a2a2a",borderRadius:7,padding:"14px 16px",fontSize:11,color:"#8a8070",lineHeight:1.6,overflowX:"auto",margin:0,fontFamily:"'Courier New',monospace",whiteSpace:"pre-wrap"}}>{APPS_SCRIPT_CODE}</pre>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── ADMIN: MEDIA EDITOR ───────────────────────────────────────────────────────

function MediaAdmin({heroImage,onHeroChange,logoImage,onLogoChange,carouselImages,onCarouselChange}) {
  const [heroUrl,setHeroUrl]=useState(heroImage||"");
  const [logoUrl,setLogoUrl]=useState(logoImage||"");
  const [newImgUrl,setNewImgUrl]=useState(""), [newImgAlt,setNewImgAlt]=useState("");
  const [heroFlash,setHeroFlash]=useState(false), [logoFlash,setLogoFlash]=useState(false), [carFlash,setCarFlash]=useState(false);

  const saveHero=async()=>{
    await saveData("vb_hero",heroUrl.trim());
    onHeroChange(heroUrl.trim());
    setHeroFlash(true); setTimeout(()=>setHeroFlash(false),1800);
  };

  const saveLogo=async()=>{
    await saveData("vb_logo",logoUrl.trim());
    onLogoChange(logoUrl.trim());
    setLogoFlash(true); setTimeout(()=>setLogoFlash(false),1800);
  };

  const addImage=async()=>{
    const url=newImgUrl.trim(); if(!url) return;
    const updated=[...carouselImages,{id:"img"+Date.now(),url,alt:newImgAlt.trim()||"Artkhoj venue"}];
    await saveData("vb_carousel",updated); onCarouselChange(updated);
    setNewImgUrl(""); setNewImgAlt("");
    setCarFlash(true); setTimeout(()=>setCarFlash(false),1800);
  };

  const removeImage=async id=>{
    const updated=carouselImages.filter(i=>i.id!==id);
    await saveData("vb_carousel",updated); onCarouselChange(updated);
  };

  const moveImage=async(idx,dir)=>{
    const a=[...carouselImages],to=idx+dir;
    if(to<0||to>=a.length) return;
    [a[idx],a[to]]=[a[to],a[idx]];
    await saveData("vb_carousel",a); onCarouselChange(a);
  };

  return (
    <div>
      {/* Logo image */}
      <div style={{background:"#141414",border:"1px solid #2a2a2a",borderRadius:10,padding:20,maxWidth:600,marginBottom:28}}>
        <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:4}}>
          <span style={{fontSize:16}}>🏷</span>
          <span style={{fontSize:13,fontWeight:700,color:"#E8E0D0"}}>Venue logo</span>
          {logoFlash&&<span style={{fontSize:11,color:"#6BAE75"}}>✓ Saved</span>}
        </div>
        <div style={{fontSize:12,color:"#5a5244",marginBottom:14,lineHeight:1.6}}>
          Shown above "About the space" on the landing page and as a subtle watermark on the booking confirmation. Use a PNG with a <strong style={{color:"#9a8e7e"}}>transparent background</strong> — a white or gold version works best on the dark background. Upload to Firebase Storage and paste the URL here.
        </div>
        <input placeholder="https://firebasestorage.googleapis.com/.../logo.png" value={logoUrl} onChange={e=>setLogoUrl(e.target.value)} style={S.inp}/>
        {logoUrl&&(
          <div style={{display:"flex",alignItems:"center",justifyContent:"center",background:"#0a0a0a",borderRadius:7,marginBottom:12,padding:16,border:"1px solid #2a2a2a",minHeight:80}}>
            <img src={logoUrl} alt="logo preview" style={{maxHeight:70,maxWidth:"50%",objectFit:"contain"}} onError={e=>e.target.style.display="none"}/>
          </div>
        )}
        <div style={{display:"flex",gap:8}}>
          <button onClick={saveLogo} style={{...S.btnP,padding:"8px 20px",fontSize:13}}>Save logo</button>
          {logoImage&&<button onClick={async()=>{await saveData("vb_logo","");onLogoChange("");setLogoUrl("");}} style={{...S.btnS,padding:"8px 16px",fontSize:13}}>Remove</button>}
        </div>
      </div>
      {/* Hero image */}
      <div style={{background:"#141414",border:"1px solid #2a2a2a",borderRadius:10,padding:20,maxWidth:600,marginBottom:28}}>
        <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:4}}>
          <span style={{fontSize:16}}>🖼</span>
          <span style={{fontSize:13,fontWeight:700,color:"#E8E0D0"}}>Header image</span>
          {heroFlash&&<span style={{fontSize:11,color:"#6BAE75"}}>✓ Saved</span>}
        </div>
        <div style={{fontSize:12,color:"#5a5244",marginBottom:14,lineHeight:1.6}}>Appears as a background image behind the "About the space" section. Use a high-quality landscape image (min 1400px wide). Paste a direct image URL from Firebase Storage, Cloudinary, or any public image host.</div>
        <input placeholder="https://your-image-host.com/hero.jpg" value={heroUrl} onChange={e=>setHeroUrl(e.target.value)} style={S.inp}/>
        {heroUrl&&<img src={heroUrl} alt="preview" style={{width:"100%",maxHeight:160,objectFit:"cover",borderRadius:7,marginBottom:12,border:"1px solid #2a2a2a"}} onError={e=>e.target.style.display="none"}/>}
        <div style={{display:"flex",gap:8}}>
          <button onClick={saveHero} style={{...S.btnP,padding:"8px 20px",fontSize:13}}>Save hero image</button>
          {heroImage&&<button onClick={async()=>{await saveData("vb_hero","");onHeroChange("");setHeroUrl("");}} style={{...S.btnS,padding:"8px 16px",fontSize:13}}>Remove</button>}
        </div>
      </div>

      {/* Carousel images */}
      <div style={{background:"#141414",border:"1px solid #2a2a2a",borderRadius:10,padding:20,maxWidth:600}}>
        <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:4}}>
          <span style={{fontSize:16}}>🎠</span>
          <span style={{fontSize:13,fontWeight:700,color:"#E8E0D0"}}>Venue glimpses carousel</span>
          {carFlash&&<span style={{fontSize:11,color:"#6BAE75"}}>✓ Saved</span>}
        </div>
        <div style={{fontSize:12,color:"#5a5244",marginBottom:16,lineHeight:1.6}}>Images shown in the carousel on the landing page. Add as many as you like — they auto-rotate every 4 seconds. Use landscape images (16:9 ratio works best).</div>

        {/* Existing images */}
        {carouselImages.length===0
          ? <div style={{fontSize:13,color:"#3a3530",textAlign:"center",padding:"20px 0",marginBottom:16}}>No images yet — add the first one below</div>
          : <div style={{display:"flex",flexDirection:"column",gap:10,marginBottom:16}}>
              {carouselImages.map((img,idx)=>(
                <div key={img.id} style={{display:"flex",alignItems:"center",gap:10,background:"#0a0a0a",borderRadius:8,padding:"10px 12px"}}>
                  <img src={img.url} alt={img.alt} loading={idx===0?"eager":"lazy"} fetchPriority={idx===0?"high":"low"} style={{width:72,height:46,objectFit:"cover",borderRadius:5,flexShrink:0,border:"1px solid #2a2a2a"}} onError={e=>e.target.style.opacity="0.2"}/>
                  <div style={{flex:1,minWidth:0}}>
                    <div style={{fontSize:12,color:"#c0b8a8",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{img.alt||"Venue image"}</div>
                    <div style={{fontSize:10,color:"#3a3530",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{img.url}</div>
                  </div>
                  <div style={{display:"flex",flexDirection:"column",gap:0,flexShrink:0}}>
                    <button onClick={()=>moveImage(idx,-1)} disabled={idx===0} style={S.arrBtn(idx===0)}>&#9650;</button>
                    <button onClick={()=>moveImage(idx,1)} disabled={idx===carouselImages.length-1} style={S.arrBtn(idx===carouselImages.length-1)}>&#9660;</button>
                  </div>
                  <button onClick={()=>removeImage(img.id)} style={{background:"none",border:"1px solid #B85450",color:"#B85450",padding:"4px 10px",borderRadius:5,cursor:"pointer",fontSize:12,fontFamily:"'Outfit',sans-serif",flexShrink:0}}>Remove</button>
                </div>
              ))}
            </div>
        }

        {/* Add new image */}
        <div style={{borderTop:"1px solid #2a2a2a",paddingTop:14}}>
          <div style={{fontSize:11,color:"#5a5244",marginBottom:8,letterSpacing:"0.06em",textTransform:"uppercase"}}>Add image</div>
          <input placeholder="Image URL (e.g. https://storage.googleapis.com/...)" value={newImgUrl} onChange={e=>setNewImgUrl(e.target.value)} onKeyDown={e=>e.key==="Enter"&&addImage()} style={S.inp}/>
          <input placeholder="Caption / alt text (optional)" value={newImgAlt} onChange={e=>setNewImgAlt(e.target.value)} onKeyDown={e=>e.key==="Enter"&&addImage()} style={{...S.inp,marginBottom:12}}/>
          <button onClick={addImage} disabled={!newImgUrl.trim()} style={{...S.btnP,...(!newImgUrl.trim()?S.btnDis:{}),padding:"8px 20px",fontSize:13}}>+ Add image</button>
        </div>
      </div>
    </div>
  );
}

// ── ADMIN: BOOKING COUNTER EDITOR ────────────────────────────────────────────

function BookingCounterEditor({bookingPrefix,bookingCounter,onBookingPrefixChange,onBookingCounterChange}) {
  const [draftPrefix,setDraftPrefix]=useState(bookingPrefix||"VB");
  const [draftCounter,setDraftCounter]=useState(String(bookingCounter||1));
  const [flash,setFlash]=useState(""), [bErr,setBErr]=useState("");

  return (
    <div>
      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12,marginBottom:10}}>
        <div>
          <div style={{fontSize:11,color:"#5a5244",marginBottom:6,letterSpacing:"0.06em",textTransform:"uppercase"}}>Prefix</div>
          <input placeholder="e.g. AK, JP, ART" value={draftPrefix} maxLength={6}
            onChange={e=>{setDraftPrefix(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g,""));setBErr("");}}
            style={{...S.inp,marginBottom:0,letterSpacing:"0.1em",fontWeight:600}}/>
        </div>
        <div>
          <div style={{fontSize:11,color:"#5a5244",marginBottom:6,letterSpacing:"0.06em",textTransform:"uppercase"}}>Starting number</div>
          <input type="number" min="1" placeholder="e.g. 1, 50, 100" value={draftCounter}
            onChange={e=>{setDraftCounter(e.target.value.replace(/\D/g,""));setBErr("");}}
            style={{...S.inp,marginBottom:0}}/>
        </div>
      </div>
      <div style={{fontSize:11,color:"#3a3530",marginBottom:12}}>
        Preview: <strong style={{color:"#c0b8a8",letterSpacing:"0.08em"}}>{(draftPrefix||"VB").toUpperCase()}-{String(parseInt(draftCounter,10)||1).padStart(3,"0")}</strong>
      </div>
      {bErr&&<div style={{color:"#B85450",fontSize:12,marginBottom:10}}>{bErr}</div>}
      <div style={{display:"flex",alignItems:"center",gap:10}}>
        <button onClick={async()=>{
          const p=draftPrefix.trim().toUpperCase();
          const c=parseInt(draftCounter,10);
          if(!p) return setBErr("Prefix cannot be empty.");
          if(!c||c<1) return setBErr("Starting number must be at least 1.");
          await saveData("vb_booking_counter",{prefix:p,counter:c});
          onBookingPrefixChange(p); onBookingCounterChange(c);
          setFlash("Saved"); setTimeout(()=>setFlash(""),2000);
        }} style={{...S.btnP,padding:"9px 22px",fontSize:13}}>Save</button>
        {flash&&<span style={{fontSize:12,color:"#6BAE75"}}>✓ {flash}</span>}
      </div>
    </div>
  );
}


// ── ADMIN: DISCOUNTS EDITOR ───────────────────────────────────────────────────

function DiscountsEditor({discounts,onChange}) {
  const [code,setCode]=useState(""), [type,setType]=useState("fixed"), [value,setValue]=useState("");
  const [flash,setFlash]=useState(""), [err,setErr]=useState("");

  const persist=async upd=>{ onChange(upd); await saveData("vb_discounts",upd); };

  const add=async()=>{
    const c=code.trim().toUpperCase();
    if(!c||c.length<3) return setErr("Code must be at least 3 characters.");
    if(!/^[A-Z0-9]+$/.test(c)) return setErr("Code must be letters and numbers only.");
    const v=parseFloat(value);
    if(!v||v<=0) return setErr("Please enter a valid amount or percentage.");
    if(type==="percent"&&v>100) return setErr("Percentage cannot exceed 100.");
    const hash=await hashCode(c);
    if(discounts.some(d=>d.hash===hash)) return setErr("This code already exists.");
    const label=c.substring(0,3)+(c.length>3?"***":"");
    const updated=[...discounts,{id:"DC"+Date.now(),hash,label,type,value:v,active:true}];
    await persist(updated);
    setCode(""); setValue(""); setErr("");
    setFlash("Added"); setTimeout(()=>setFlash(""),2000);
  };

  const toggle=async id=>persist(discounts.map(d=>d.id===id?{...d,active:!d.active}:d));
  const remove=async id=>persist(discounts.filter(d=>d.id!==id));

  return (
    <div>
      <div style={{background:"#141414",border:"1px solid #2a2a2a",borderRadius:10,padding:20,maxWidth:520,marginBottom:24}}>
        <div style={{fontSize:13,fontWeight:700,color:"#E8E0D0",marginBottom:16}}>Create discount code</div>
        <div style={{display:"grid",gridTemplateColumns:"2fr 1fr 1fr",gap:10,marginBottom:10}}>
          <div>
            <div style={{fontSize:11,color:"#5a5244",marginBottom:6,letterSpacing:"0.06em",textTransform:"uppercase"}}>Code</div>
            <input placeholder="e.g. EARLY500" value={code} onChange={e=>{setCode(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g,""));setErr("");}} style={{...S.inp,marginBottom:0,letterSpacing:"0.1em",fontWeight:600}}/>
          </div>
          <div>
            <div style={{fontSize:11,color:"#5a5244",marginBottom:6,letterSpacing:"0.06em",textTransform:"uppercase"}}>Type</div>
            <select value={type} onChange={e=>setType(e.target.value)} style={{...S.inp,marginBottom:0}}>
              <option value="fixed">Fixed ₹</option>
              <option value="percent">Percent %</option>
            </select>
          </div>
          <div>
            <div style={{fontSize:11,color:"#5a5244",marginBottom:6,letterSpacing:"0.06em",textTransform:"uppercase"}}>{type==="fixed"?"Amount (₹)":"Percent (%)"}</div>
            <input type="number" placeholder={type==="fixed"?"500":"20"} value={value} onChange={e=>{setValue(e.target.value);setErr("");}} style={{...S.inp,marginBottom:0}}/>
          </div>
        </div>
        {err&&<div style={{color:"#B85450",fontSize:12,marginBottom:10}}>{err}</div>}
        <div style={{display:"flex",alignItems:"center",gap:10}}>
          <button onClick={add} style={{...S.btnP,padding:"8px 22px",fontSize:13}}>+ Add code</button>
          {flash&&<span style={{fontSize:12,color:"#6BAE75"}}>✓ {flash}</span>}
        </div>
        <div style={{fontSize:11,color:"#3a3530",marginTop:10,lineHeight:1.6}}>
          Codes are stored as hashes — once saved, the full code cannot be recovered. Note it down before saving.
        </div>
      </div>

      {discounts.length===0
        ? <div style={S.empty}>No discount codes yet</div>
        : <div style={{background:"#141414",border:"1px solid #2a2a2a",borderRadius:10,overflow:"hidden"}}>
            <div style={{display:"grid",gridTemplateColumns:"1fr 100px 90px 80px 80px",gap:0,padding:"10px 16px",borderBottom:"1px solid #2a2a2a"}}>
              {["Code","Type","Value","Status",""].map(h=><div key={h} style={{fontSize:11,color:"#5a5244",letterSpacing:"0.08em",textTransform:"uppercase"}}>{h}</div>)}
            </div>
            {discounts.map(d=>(
              <div key={d.id} style={{display:"grid",gridTemplateColumns:"1fr 100px 90px 80px 80px",gap:0,padding:"12px 16px",borderBottom:"1px solid #1e1e1e",alignItems:"center"}}>
                <span style={{fontSize:14,fontWeight:700,color:"#C9A84C",letterSpacing:"0.1em"}}>{d.label}</span>
                <span style={{fontSize:12,color:"#c0b8a8"}}>{d.type==="fixed"?"Fixed ₹":"Percent %"}</span>
                <span style={{fontSize:14,color:"#E8E0D0",fontWeight:600}}>{d.type==="fixed"?`₹${d.value.toLocaleString("en-IN")}`:`${d.value}%`}</span>
                <button onClick={()=>toggle(d.id)} style={{background:d.active?"#0d1a0f":"#1a0d0d",border:`1px solid ${d.active?"#1e3820":"#3a1e1e"}`,color:d.active?"#6BAE75":"#B85450",padding:"4px 10px",borderRadius:5,cursor:"pointer",fontSize:11,fontFamily:"'Outfit',sans-serif",fontWeight:600}}>
                  {d.active?"Active":"Inactive"}
                </button>
                <button onClick={()=>remove(d.id)} style={{background:"none",border:"1px solid #B85450",color:"#B85450",padding:"4px 10px",borderRadius:5,cursor:"pointer",fontSize:11,fontFamily:"'Outfit',sans-serif"}}>Delete</button>
              </div>
            ))}
          </div>
      }
    </div>
  );
}



// ── ADMIN APP ─────────────────────────────────────────────────────────────────

function AdminApp({onExit,facilities,onFacilitiesChange,capacity,onCapacityChange,pricing,onPricingChange,perfTypes,onPerfTypesChange,tech,onTechChange,onAppBookingsChange,onAppBlockedChange,gcalConfig,onGcalChange,heroImage,onHeroChange,logoImage,onLogoChange,carouselImages,onCarouselChange,leads,onLeadsChange,discounts,onDiscountsChange,bookingPrefix,onBookingPrefixChange,bookingCounter,onBookingCounterChange}) {
  const [auth,setAuth]=useState(false), [role,setRole]=useState(null);
  const [username,setUsername]=useState(""), [pwd,setPwd]=useState(""), [loginErr,setLoginErr]=useState("");
  const [tab,setTab]=useState("block"), [bookings,setBookings]=useState([]), [blocked,setBlocked]=useState([]);
  const [blockDate,setBlockDate]=useState(""), [allDay,setAllDay]=useState(true), [bStart,setBStart]=useState(8), [bEnd,setBEnd]=useState(10), [bReason,setBReason]=useState(""), [bComment,setBComment]=useState("");
                const [bClientName,setBClientName]=useState(""), [bClientEmail,setBClientEmail]=useState(""), [bClientPhone,setBClientPhone]=useState("");
  const [bEventType,setBEventType]=useState(""), [bAmount,setBAmount]=useState("");
  const [bSendEmail,setBSendEmail]=useState(false), [bMoveToBookings,setBMoveToBookings]=useState(false);
  const [editBkId,setEditBkId]=useState(null), [confirmDeleteId,setConfirmDeleteId]=useState(null), [confirmCancelId,setConfirmCancelId]=useState(null);
  const [xlFrom,setXlFrom]=useState(""), [xlTo,setXlTo]=useState("");
  const [leadFilter,setLeadFilter]=useState("all");
  const [storedPwd,setStoredPwd]=useState("venue@2024");
  const [storedManagerPwd,setStoredManagerPwd]=useState("manager@2024");
  const [curPwd,setCurPwd]=useState(""), [newPwd,setNewPwd]=useState(""), [confirmPwd,setConfirmPwd]=useState(""), [pwdMsg,setPwdMsg]=useState(null);
  const [mgrCurPwd,setMgrCurPwd]=useState(""), [mgrNewPwd,setMgrNewPwd]=useState(""), [mgrConfirmPwd,setMgrConfirmPwd]=useState(""), [mgrPwdMsg,setMgrPwdMsg]=useState(null);

  useEffect(()=>{ if(auth){ loadData("vb_b").then(setBookings); loadData("vb_bl").then(setBlocked); } },[auth]);
  useEffect(()=>{
    loadData("vb_pwd","venue@2024").then(v=>{ if(typeof v==="string"&&v.length>0) setStoredPwd(v); });
    loadData("vb_pwd_manager","manager@2024").then(v=>{ if(typeof v==="string"&&v.length>0) setStoredManagerPwd(v); });
  },[]);

  const login=()=>{
    const u=username.trim().toLowerCase();
    if(u==="admin"&&pwd===storedPwd){ setAuth(true); setRole("admin"); setLoginErr(""); setTab("block"); }
    else if(u==="manager"&&pwd===storedManagerPwd){ setAuth(true); setRole("manager"); setLoginErr(""); setTab("block"); }
    else setLoginErr("Incorrect username or password.");
  };
  const syncBlocked  = upd=>{ setBlocked(upd); if(onAppBlockedChange) onAppBlockedChange(upd); };
  const syncBookings = (bks,bls)=>{ setBookings(bks); setBlocked(bls); if(onAppBookingsChange) onAppBookingsChange(bks); if(onAppBlockedChange) onAppBlockedChange(bls); };

   const resetBlockForm=()=>{ setBlockDate("");setAllDay(true);setBStart(8);setBEnd(10);setBReason("");setBComment(""); setBClientName("");setBClientEmail("");setBClientPhone("");setBEventType("");setBAmount("");setBSendEmail(false);setBMoveToBookings(false); };

  const addBlock=async()=>{
    if(!blockDate) return;

    if(bMoveToBookings){
      const cfg=await loadData("vb_booking_counter",null);
      const prefix=(cfg&&cfg.prefix)?cfg.prefix.toUpperCase():(bookingPrefix||"VB");
      const counter=(cfg&&cfg.counter&&cfg.counter>0)?cfg.counter:(bookingCounter||1);
      const seqId=`${prefix}-${String(counter).padStart(3,"0")}`;
      await saveData("vb_booking_counter",{prefix,counter:counter+1});
      onBookingPrefixChange(prefix); onBookingCounterChange(counter+1);
      const bk={
        id:seqId, date:blockDate,
        start:allDay?8:bStart, end:allDay?23:bEnd,
        eventType:bEventType||"Performance",
        customerName:bClientName||"",
        customerEmail:bSendEmail?(bClientEmail||""):"",
        customerPhone:bClientPhone||"",
        eventDescription:[bReason,bComment.trim()].filter(Boolean).join("\n")||"",
        total:parseFloat(bAmount)||0, base:parseFloat(bAmount)||0, gst:0,
        paymentMethod:"offline", manualEntry:true,
        bookedAt:new Date().toISOString(),
      };
      const newBks=[...bookings,bk];
      const newBls=applyBuffer(bk,newBks,blocked);
      await saveData("vb_b",newBks);
      if(newBls.length!==blocked.length) await saveData("vb_bl",newBls);
      syncBookings(newBks,newBls);
      syncToGCal(bk,gcalConfig,perfTypes);
      resetBlockForm(); return;
    }

    const b={id:"BL"+Date.now(),date:blockDate,allDay,start:allDay?8:bStart,end:allDay?23:bEnd,reason:bReason||"Maintenance",comment:bComment.trim()||""};
    const upd=[...blocked,b];
    await saveData("vb_bl",upd); syncBlocked(upd);
    if(!(b.reason?.toLowerCase().includes("buffer"))) syncBlockToGCal(b,gcalConfig);
    resetBlockForm();
  };
  const removeBlock=async id=>{ const upd=blocked.filter(b=>b.id!==id); await saveData("vb_bl",upd); syncBlocked(upd); };

  const saveBookingEdit=async updated=>{
    const bk={...updated,rescheduledAt:new Date().toISOString()};
    const newBks=bookings.map(b=>b.id===bk.id?bk:b);
    const newBls=applyBuffer(bk,newBks,blocked.filter(b=>b.bookingId!==updated.id));
    await saveData("vb_b",newBks); await saveData("vb_bl",newBls);
    syncBookings(newBks,newBls); setEditBkId(null);
  };

    const deleteBooking=async id=>{
    const bk=bookings.find(b=>b.id===id);
    const newBks=bookings.filter(b=>b.id!==id), newBls=blocked.filter(b=>b.bookingId!==id);
    await saveData("vb_b",newBks); if(newBls.length!==blocked.length) await saveData("vb_bl",newBls);
    syncBookings(newBks,newBls); setConfirmDeleteId(null); if(editBkId===id) setEditBkId(null);
    if(bk&&!bk.cancelled) syncDeleteToGCal(bk,gcalConfig);
  };

  const cancelBooking=async id=>{
    const bk=bookings.find(b=>b.id===id); if(!bk||bk.cancelled) return;
    const newBks=bookings.map(b=>b.id===id?{...b,cancelled:true,cancelledAt:new Date().toISOString()}:b);
    const newBls=blocked.filter(b=>b.bookingId!==id);
    await saveData("vb_b",newBks);
    if(newBls.length!==blocked.length) await saveData("vb_bl",newBls);
    syncBookings(newBks,newBls); setConfirmCancelId(null);
    syncCancelToGCal(bk,gcalConfig,perfTypes);
  };


// Code for Admin portal UI Screen
 if(!auth) return (
    <div style={{minHeight:"100vh",display:"flex",alignItems:"center",justifyContent:"center",background:"#0a0a0a"}}>
      <div style={{background:"#141414",border:"1px solid #2a2a2a",borderRadius:16,padding:"40px 36px",width:360,textAlign:"center"}}>
        <div style={{fontSize:42,marginBottom:12}}>&#127963;</div>
        <h2 style={{fontFamily:"'Cormorant Garamond',serif",fontSize:28,marginBottom:6,color:"#E8E0D0"}}>Admin Portal</h2>
        <p style={{fontSize:13,color:"#5a5244",marginBottom:24, textAlign:"center"}}>Sign in</p>
        <input placeholder="Username" value={username} onChange={e=>setUsername(e.target.value)} onKeyDown={e=>e.key==="Enter"&&login()} style={{...S.inp,marginBottom:8,textAlign:"center"}}/>
        <input type="password" placeholder="Password" value={pwd} onChange={e=>setPwd(e.target.value)} onKeyDown={e=>e.key==="Enter"&&login()} style={{...S.inp,marginBottom:8,textAlign:"center"}}/>
        {loginErr&&<div style={{color:"#B85450",fontSize:13,marginBottom:10}}>{loginErr}</div>}
        <button onClick={login} style={{...S.btnP,width:"100%",marginBottom:14}}>Sign In</button>
        <button onClick={onExit} style={{background:"none",border:"none",color:"#5a5244",fontSize:13,cursor:"pointer"}}>&#8592; Back to booking</button>
      </div>
    </div>
  );

  const ALL_TABS=[{key:"block",label:"Block Slots"},{key:"pricing",label:"Pricing"},{key:"facilities",label:"Facilities"},{key:"media",label:"Media"},{key:"discounts",label:"Venue Controls"},{key:"leads",label:`Leads (${leads.length})`},{key:"bookings",label:`Bookings (${bookings.length})`},{key:"settings",label:"Settings"}];
  const MANAGER_TABS=["block","leads","bookings"];
  const TABS=role==="admin" ? ALL_TABS : ALL_TABS.filter(t=>MANAGER_TABS.includes(t.key));
  const adminH3=txt=><h3 style={{fontFamily:"'Cormorant Garamond',serif",fontSize:22,marginBottom:14}}>{txt}</h3>;
  const subLabel=txt=><div style={{fontSize:11,fontWeight:700,letterSpacing:"0.1em",textTransform:"uppercase",color:"#5a5244",marginBottom:12}}>{txt}</div>;

  return (
    <div style={{minHeight:"100vh",background:"#0a0a0a"}}>
      <div style={{background:"#141414",borderBottom:"1px solid #2a2a2a",padding:"13px 24px",display:"flex",alignItems:"center",gap:16,flexWrap:"wrap"}}>
        <span style={{fontFamily:"'Cormorant Garamond',serif",fontSize:18,fontWeight:600,flex:1}}>
          &#127963; Venue Admin
          <span style={{marginLeft:10,fontSize:11,fontWeight:400,letterSpacing:"0.08em",textTransform:"uppercase",color:role==="admin"?"#C9A84C":"#6BAE75",border:`1px solid ${role==="admin"?"#C9A84C":"#6BAE75"}`,padding:"2px 8px",borderRadius:10}}>
            {role==="admin"?"Super Admin":"Manager"}
          </span>
        </span>
        <div style={{display:"flex",gap:4}}>
          {TABS.map(t=><button key={t.key} onClick={()=>setTab(t.key)} style={{padding:"7px 16px",borderRadius:6,border:`1px solid ${tab===t.key?"#C9A84C":"transparent"}`,background:tab===t.key?"#1a1500":"none",color:tab===t.key?"#C9A84C":"#6b6356",cursor:"pointer",fontSize:13,fontFamily:"'Outfit',sans-serif",whiteSpace:"nowrap"}}>{t.label}</button>)}
        </div>
        <button onClick={onExit} style={{background:"none",border:"none",color:"#5a5244",fontSize:13,cursor:"pointer"}}>&#8592; Exit</button>
      </div>

      <div style={{maxWidth:900,margin:"0 auto",padding:"32px 24px"}}>

                {tab==="block"&&(
          <div>
            {adminH3("Block a date or time slot")}
            <div style={{background:"#141414",border:"1px solid #2a2a2a",borderRadius:10,padding:20,maxWidth:520,marginBottom:28}}>

              <div style={{fontSize:11,color:"#5a5244",marginBottom:6,letterSpacing:"0.06em",textTransform:"uppercase"}}>Date</div>
              <input type="date" value={blockDate} onChange={e=>setBlockDate(e.target.value)} min={todayStr()} style={S.inp}/>
              <label style={{display:"flex",alignItems:"center",gap:8,fontSize:14,color:"#9a8e7e",cursor:"pointer",marginBottom:10}}>
                <input type="checkbox" checked={allDay} onChange={e=>setAllDay(e.target.checked)} style={{accentColor:"#C9A84C"}}/>Block entire day
              </label>
              {!allDay&&<div style={{display:"flex",alignItems:"center",gap:10,marginBottom:10}}>
                <select value={bStart} onChange={e=>setBStart(+e.target.value)} style={{...S.inp,flex:1,marginBottom:0}}>{HALF_HOURS_START.map(h=><option key={h} value={h}>{fmtH(h)}</option>)}</select>
                <span style={{color:"#5a5244"}}>to</span>
                <select value={bEnd} onChange={e=>setBEnd(+e.target.value)} style={{...S.inp,flex:1,marginBottom:0}}>{HALF_HOURS_END.map(h=><option key={h} value={h}>{fmtH(h)}</option>)}</select>
              </div>}
              <input placeholder="Reason (e.g. Maintenance, Private Event)" value={bReason} onChange={e=>setBReason(e.target.value)} style={S.inp}/>
              <textarea placeholder="Additional comments — internal notes, instructions..." value={bComment} onChange={e=>setBComment(e.target.value)} rows={2} style={{...S.inp,resize:"vertical",lineHeight:1.6}}/>

              <div style={{borderTop:"1px solid #2a2a2a",margin:"14px 0"}}/>
              <div style={{fontSize:11,color:"#9a8e7e",fontWeight:700,letterSpacing:"0.1em",textTransform:"uppercase",marginBottom:12}}>Client details (offline / phone booking)</div>

              <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10}}>
                <div>
                  <div style={{fontSize:11,color:"#5a5244",marginBottom:5,letterSpacing:"0.05em",textTransform:"uppercase"}}>Client name</div>
                  <input placeholder="Full name" value={bClientName} onChange={e=>setBClientName(e.target.value)} style={{...S.inp,marginBottom:0}}/>
                </div>
                <div>
                  <div style={{fontSize:11,color:"#5a5244",marginBottom:5,letterSpacing:"0.05em",textTransform:"uppercase"}}>Phone</div>
                  <input placeholder="Phone number" value={bClientPhone} onChange={e=>setBClientPhone(e.target.value)} style={{...S.inp,marginBottom:0}}/>
                </div>
              </div>
              <div style={{marginTop:10}}>
                <div style={{fontSize:11,color:"#5a5244",marginBottom:5,letterSpacing:"0.05em",textTransform:"uppercase"}}>Email</div>
                <input placeholder="client@email.com" value={bClientEmail} onChange={e=>setBClientEmail(e.target.value)} style={{...S.inp,marginBottom:0}}/>
              </div>
              <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10,marginTop:10}}>
                <div>
                  <div style={{fontSize:11,color:"#5a5244",marginBottom:5,letterSpacing:"0.05em",textTransform:"uppercase"}}>Event type</div>
                  <select value={bEventType} onChange={e=>setBEventType(e.target.value)} style={{...S.inp,marginBottom:0}}>
                    <option value="">Select type</option>
                    {Object.keys(DEFAULT_EVENTS).map(k=><option key={k} value={k}>{DEFAULT_EVENTS[k].icon} {k}</option>)}
                  </select>
                </div>
                <div>
                  <div style={{fontSize:11,color:"#5a5244",marginBottom:5,letterSpacing:"0.05em",textTransform:"uppercase"}}>Amount paid (₹)</div>
                  <input type="number" placeholder="e.g. 5000" value={bAmount} onChange={e=>setBAmount(e.target.value)} style={{...S.inp,marginBottom:0}}/>
                </div>
              </div>

              <div style={{borderTop:"1px solid #2a2a2a",margin:"14px 0"}}/>
              <div style={{display:"flex",flexDirection:"column",gap:10}}>
                <label style={{display:"flex",alignItems:"flex-start",gap:10,cursor:"pointer"}}>
                  <input type="checkbox" checked={bMoveToBookings} onChange={e=>setBMoveToBookings(e.target.checked)} style={{accentColor:"#C9A84C",marginTop:2}}/>
                  <div>
                    <div style={{fontSize:13,color:"#E8E0D0",fontWeight:500}}>Move to bookings</div>
                    <div style={{fontSize:11,color:"#5a5244",marginTop:2,lineHeight:1.5}}>Generates a booking ID, adds to the Bookings tab with auto-buffers, and syncs to Google Calendar.</div>
                  </div>
                </label>
                <label style={{display:"flex",alignItems:"flex-start",gap:10,cursor:bMoveToBookings?"pointer":"not-allowed",opacity:bMoveToBookings?1:0.4}}>
                  <input type="checkbox" checked={bSendEmail} disabled={!bMoveToBookings} onChange={e=>setBSendEmail(e.target.checked)} style={{accentColor:"#C9A84C",marginTop:2}}/>
                  <div>
                    <div style={{fontSize:13,color:"#E8E0D0",fontWeight:500}}>Send confirmation email to client</div>
                    <div style={{fontSize:11,color:"#5a5244",marginTop:2,lineHeight:1.5}}>Sends the same booking confirmation email the client receives after online payment. Requires client email above. Only available when "Move to bookings" is selected.</div>
                  </div>
                </label>
              </div>

              <button onClick={addBlock} disabled={!blockDate} style={{...S.btnP,width:"100%",marginTop:16,...(!blockDate?S.btnDis:{})}}>
                {bMoveToBookings?"Add to Bookings":"Add Block"}
              </button>
            </div>

            {adminH3(`Blocked slots (${blocked.length})`)}
            {blocked.length===0?<div style={S.empty}>No blocked slots yet</div>:blocked.map(b=>(
              <div key={b.id} style={{display:"flex",alignItems:"flex-start",justifyContent:"space-between",background:"#141414",border:`1px solid ${b.isBuffer?"#1e2a1e":"#2a2a2a"}`,borderRadius:8,padding:"14px 16px",marginBottom:8}}>
                <div>
                  <div style={{fontWeight:600,fontSize:14,marginBottom:4}}>{fmtShort(b.date)}</div>
                  <div style={{fontSize:12,color:"#6b6356"}}>{b.allDay?"Full day":`${fmtH(b.start)} – ${fmtH(b.end)}`} &middot; {b.reason}</div>
                  {b.comment&&<div style={{fontSize:12,color:"#5a5244",marginTop:4,fontStyle:"italic"}}>"{b.comment}"</div>}
                  {b.isBuffer&&<div style={{fontSize:10,color:"#4a6e4a",marginTop:3,letterSpacing:"0.06em",textTransform:"uppercase"}}>Auto buffer</div>}
                </div>
                {!b.isBuffer&&<button onClick={()=>removeBlock(b.id)} style={{background:"none",border:"1px solid #B85450",color:"#B85450",padding:"6px 14px",borderRadius:6,cursor:"pointer",fontSize:12,fontFamily:"'Outfit',sans-serif",flexShrink:0,marginLeft:12}}>Remove</button>}
              </div>
            ))}
          </div>
        )}

        {tab==="pricing"&&(
          <div>
            {adminH3("Pricing")}
            <p style={{fontSize:13,color:"#5a5244",marginBottom:24,lineHeight:1.7}}>Set the base rate (first 2 hours) and additional hourly charge per booking type. Prices are exclusive of GST — the preview shows customer-facing totals. Click <strong style={{color:"#9a8e7e"}}>Save prices</strong> to apply.</p>
            <PricingEditor pricing={pricing} onChange={onPricingChange}/>
          </div>
        )}

        {tab==="facilities"&&(
          <div>
            {adminH3("Manage Venue Facilities")}
            <p style={{fontSize:13,color:"#5a5244",marginBottom:22,lineHeight:1.7}}>Customise seating capacities, performance sub-types, tech availability, and per-booking-type facility lists.</p>
            {subLabel("Venue-wide seating capacity (landing page)")}
            <RowListEditor title="Seating capacity" storageKey="vb_cap" defaultData={DEFAULT_CAPACITY} nameField="format" rows={capacity} onChange={onCapacityChange}/>
            {subLabel("Performance types & seating (booking review)")}
            <RowListEditor title="🎭 Performance sub-types" storageKey="vb_ptypes" defaultData={DEFAULT_PERF_TYPES} nameField="name" rows={perfTypes} onChange={onPerfTypesChange}/>
            {subLabel("Tech available (performance bookings only)")}
            <TechEditor tech={tech} onChange={onTechChange}/>
            {subLabel("Included & excluded items per booking type")}
            <FacilitiesEditor facilities={facilities} onChange={onFacilitiesChange}/>
          </div>
        )}

        {tab==="media"&&(
          <div>
            {adminH3("Media")}
            <p style={{fontSize:13,color:"#5a5244",marginBottom:24,lineHeight:1.7}}>Manage the venue logo, hero image, and photo carousel. Paste direct image URLs from Firebase Storage, Cloudinary, or any public image host.</p>
            <MediaAdmin heroImage={heroImage} onHeroChange={onHeroChange} logoImage={logoImage} onLogoChange={onLogoChange} carouselImages={carouselImages} onCarouselChange={onCarouselChange}/>
          </div>
        )}

        {tab==="discounts"&&(
          <div>
            {adminH3("Venue Controls")}

            {/* Booking sequence ID */}
            <div style={{background:"#141414",border:"1px solid #2a2a2a",borderRadius:10,padding:20,maxWidth:520,marginBottom:32}}>
              <div style={{fontSize:13,fontWeight:700,color:"#E8E0D0",marginBottom:4}}>Booking sequence ID</div>
              <div style={{fontSize:12,color:"#5a5244",marginBottom:16,lineHeight:1.7}}>
                Set a prefix and starting number. Every confirmed booking gets an ID like <strong style={{color:"#c0b8a8"}}>AK-001</strong>, <strong style={{color:"#c0b8a8"}}>AK-002</strong> and auto-increments. Update anytime — counter continues from the saved value.
              </div>
              <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:20,padding:"12px 16px",background:"#0a0a0a",borderRadius:8,border:"1px solid #2a2a2a"}}>
                <span style={{fontSize:12,color:"#5a5244"}}>Next booking ID:</span>
                <span style={{fontFamily:"'Cormorant Garamond',serif",fontSize:24,color:"#C9A84C",fontWeight:700,letterSpacing:"0.12em"}}>
                  {(bookingPrefix||"VB").toUpperCase()}-{String(bookingCounter||1).padStart(3,"0")}
                </span>
              </div>
              <BookingCounterEditor bookingPrefix={bookingPrefix} bookingCounter={bookingCounter} onBookingPrefixChange={onBookingPrefixChange} onBookingCounterChange={onBookingCounterChange}/>
            </div>

            {/* Discount codes */}
            <div style={{fontSize:11,fontWeight:700,letterSpacing:"0.1em",textTransform:"uppercase",color:"#9a8e7e",marginBottom:16}}>Discount codes</div>
            <p style={{fontSize:13,color:"#5a5244",marginBottom:24,lineHeight:1.7}}>
              Create promo codes for customers. Codes are stored as hashes — once saved the full code cannot be recovered, so note it down first. Customers enter it on the payment page under "Have a promo code?".
            </p>
            <DiscountsEditor discounts={discounts} onChange={onDiscountsChange}/>
          </div>
        )}

                       {tab==="leads"&&(
          <div>
            {adminH3("Leads")}
            <p style={{fontSize:13,color:"#5a5244",marginBottom:20,lineHeight:1.7}}>Customers who filled in their details on the review page but may not have completed payment. Useful for follow-ups.</p>

            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:12,flexWrap:"wrap",gap:8}}>
              {/* Status filter */}
              <div style={{display:"flex",gap:6}}>
                {[["all","All",null],["new","New","#5a5244"],["booked","Booked","#6BAE75"],["lost","Didn't convert","#B85450"]].map(([val,label,col])=>(
                  <button key={val} onClick={()=>setLeadFilter(val)}
                    style={{padding:"5px 12px",borderRadius:5,fontSize:12,cursor:"pointer",fontFamily:"'Outfit',sans-serif",
                      border:`1px solid ${leadFilter===val?(col||"#C9A84C"):"#2a2a2a"}`,
                      background:leadFilter===val?"#1a1500":"none",
                      color:leadFilter===val?(col||"#C9A84C"):"#5a5244"}}>
                    {label}
                  </button>
                ))}
              </div>
              {leads.length>0&&<button onClick={async()=>{await saveData("vb_leads",[]);onLeadsChange([]);}} style={{background:"none",border:"1px solid #2a2a2a",color:"#5a5244",padding:"6px 14px",borderRadius:6,cursor:"pointer",fontSize:12,fontFamily:"'Outfit',sans-serif"}}>Clear all leads</button>}
            </div>

            {leads.length===0?<div style={S.empty}>No leads captured yet</div>:(()=>{
              const filtered=[...leads].reverse().filter(l=>leadFilter==="all"||(leadFilter==="new"?!l.status||l.status==="new":l.status===leadFilter));
              const updateStatus=async(id,status)=>{
                const updated=leads.map(l=>l.id===id?{...l,status}:l);
                await saveData("vb_leads",updated); onLeadsChange(updated);
              };
              if(filtered.length===0) return <div style={S.empty}>No leads with this status</div>;
              return (
                <div style={{overflowX:"auto"}}>
                  <table style={{width:"100%",borderCollapse:"collapse",fontSize:13}}>
                    <thead>
                      <tr style={{borderBottom:"1px solid #2a2a2a"}}>
                        {["Captured","Name","Phone","Email","Event","Date","Time","Status",""].map(h=>(
                          <th key={h} style={{textAlign:"left",padding:"10px 14px",fontSize:11,color:"#5a5244",fontWeight:700,letterSpacing:"0.08em",textTransform:"uppercase",whiteSpace:"nowrap"}}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {filtered.map((l,i)=>{
                        const status=l.status||"new";
                        const statusCfg={
                          new:  {label:"New",        color:"#5a5244", bg:"#2a2a2a"},
                          booked:{label:"Booked ✓",  color:"#6BAE75", bg:"#0d1a0f"},
                          lost: {label:"Didn't convert", color:"#B85450", bg:"#1a0d0d"},
                        }[status]||{label:"New",color:"#5a5244",bg:"#2a2a2a"};
                        return (
                          <tr key={l.id} style={{borderBottom:"1px solid #1e1e1e",background:i%2===0?"#141414":"#111"}}>
                            <td style={{padding:"10px 14px",color:"#5a5244",fontSize:12,whiteSpace:"nowrap"}}>{new Date(l.capturedAt).toLocaleDateString("en-IN",{day:"numeric",month:"short",hour:"2-digit",minute:"2-digit"})}</td>
                            <td style={{padding:"10px 14px",color:"#E8E0D0",fontWeight:500}}>{l.name}</td>
                            <td style={{padding:"10px 14px",color:"#c0b8a8"}}>{l.phone}</td>
                            <td style={{padding:"10px 14px",color:"#5B9BD5"}}><a href={`mailto:${l.email}`} style={{color:"#5B9BD5",textDecoration:"none"}}>{l.email}</a></td>
                            <td style={{padding:"10px 14px",color:"#C9A84C",fontWeight:600}}>{DEFAULT_EVENTS[l.eventType]?.icon} {l.eventType}</td>
                            <td style={{padding:"10px 14px",color:"#c0b8a8",whiteSpace:"nowrap"}}>{fmtShort(l.date)}</td>
                            <td style={{padding:"10px 14px",color:"#c0b8a8",whiteSpace:"nowrap"}}>{fmtH(l.start)} – {fmtH(l.end)}</td>
                            <td style={{padding:"10px 14px"}}>
                              <select value={status} onChange={e=>updateStatus(l.id,e.target.value)}
                                style={{background:statusCfg.bg,border:`1px solid ${statusCfg.color}`,color:statusCfg.color,padding:"4px 8px",borderRadius:5,fontSize:11,fontFamily:"'Outfit',sans-serif",cursor:"pointer",outline:"none"}}>
                                <option value="new">New</option>
                                <option value="booked">Booked ✓</option>
                                <option value="lost">Didn't convert</option>
                              </select>
                            </td>
                            <td style={{padding:"10px 14px"}}>
                              <button onClick={async()=>{
                                const updated=leads.filter(x=>x.id!==l.id);
                                await saveData("vb_leads",updated); onLeadsChange(updated);
                              }} style={{background:"none",border:"1px solid #B85450",color:"#B85450",padding:"4px 10px",borderRadius:5,cursor:"pointer",fontSize:11,fontFamily:"'Outfit',sans-serif",whiteSpace:"nowrap"}}>Delete</button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              );
            })()}
          </div>
        )}

        {tab==="bookings"&&(
          <div>
            {adminH3("All bookings")}

            {/* Excel download with date range */}
            <div style={{background:"#141414",border:"1px solid #2a2a2a",borderRadius:10,padding:"14px 18px",marginBottom:20,display:"flex",flexWrap:"wrap",alignItems:"flex-end",gap:12}}>
              <div>
                <div style={{fontSize:11,color:"#5a5244",marginBottom:6,letterSpacing:"0.06em",textTransform:"uppercase"}}>From</div>
                <input type="date" value={xlFrom} onChange={e=>setXlFrom(e.target.value)} style={{...S.inp,marginBottom:0,width:160}}/>
              </div>
              <div>
                <div style={{fontSize:11,color:"#5a5244",marginBottom:6,letterSpacing:"0.06em",textTransform:"uppercase"}}>To</div>
                <input type="date" value={xlTo} onChange={e=>setXlTo(e.target.value)} style={{...S.inp,marginBottom:0,width:160}}/>
              </div>
              <button onClick={()=>{
                const filtered=bookings.filter(bk=>{
                  if(xlFrom&&bk.date<xlFrom) return false;
                  if(xlTo&&bk.date>xlTo) return false;
                  return true;
                });
                if(filtered.length===0) return alert("No bookings found in selected date range.");
                const rows=filtered.map(bk=>({
                  "Booking ID":       bk.id||"",
                  "Date":             bk.date||"",
                  "Start":            fmtH(bk.start),
                  "End":              fmtH(bk.end),
                  "Hours":            (bk.end-bk.start),
                  "Event Type":       bk.eventType||"",
                  "Performance Type": bk.perfTypeName||"",
                  "Customer Name":    bk.customerName||"",
                  "Phone":            bk.customerPhone||"",
                  "Email":            bk.customerEmail||"",
                  "Billing Name":     bk.billing?.name||"",
                  "GST No":           bk.billing?.gstNo||"",
                  "Venue Charges":    bk.base||"",
                  "GST Amount":       bk.gst||"",
                  "Discount Code":    bk.discountCode||"",
                  "Discount Amount":  bk.discountAmount||0,
                  "Total Paid":       bk.total||"",
                  "Payment Method":   bk.paymentMethod||"",
                  "Booked At":        bk.bookedAt?new Date(bk.bookedAt).toLocaleString("en-IN"):"",
                }));
                const ws=XLSX.utils.json_to_sheet(rows);
                const wb=XLSX.utils.book_new();
                XLSX.utils.book_append_sheet(wb,ws,"Bookings");
                const fromLabel=xlFrom||"all";
                const toLabel=xlTo||"all";
                XLSX.writeFile(wb,`Artkhoj_Bookings_${fromLabel}_to_${toLabel}.xlsx`);
              }} style={{...S.btnP,padding:"10px 20px",fontSize:13,whiteSpace:"nowrap"}}>
                &#8595; Download Excel
              </button>
              {(xlFrom||xlTo)&&(
                <button onClick={()=>{setXlFrom("");setXlTo("");}} style={{...S.btnS,padding:"10px 16px",fontSize:13}}>Clear filter</button>
              )}
              <span style={{fontSize:12,color:"#5a5244",alignSelf:"center"}}>
                {(xlFrom||xlTo)
                  ? `${bookings.filter(b=>{if(xlFrom&&b.date<xlFrom)return false;if(xlTo&&b.date>xlTo)return false;return true;}).length} booking(s) in range`
                  : `${bookings.length} total booking(s)`
                }
              </span>
            </div>

                        {bookings.length===0?<div style={S.empty}>No bookings yet</div>:[...bookings].reverse().map(bk=>(
              <div key={bk.id} style={{background:"#141414",border:`1px solid ${bk.cancelled?"#3a1e1e":"#2a2a2a"}`,borderRadius:8,padding:16,marginBottom:10,opacity:bk.cancelled?0.75:1}}>
                <div style={{display:"flex",alignItems:"flex-start",justifyContent:"space-between",marginBottom:10,gap:12}}>
                  <div>
                    <div style={{fontSize:11,letterSpacing:"0.12em",fontWeight:700,marginBottom:6,display:"flex",alignItems:"center",gap:8}}>
                      <span style={{color:bk.cancelled?"#B85450":"#C9A84C"}}>{bk.id}</span>
                      {bk.cancelled&&<span style={{fontSize:10,background:"#1a0d0d",border:"1px solid #B85450",color:"#B85450",padding:"2px 8px",borderRadius:10,letterSpacing:"0.06em"}}>CANCELLED</span>}
                      {bk.rescheduledAt&&!bk.cancelled&&<span style={{fontSize:10,color:"#5B9BD5",fontWeight:400}}>Rescheduled</span>}
                      {bk.manualEntry&&!bk.cancelled&&<span style={{fontSize:10,color:"#9a8e7e",fontWeight:400}}>Offline booking</span>}
                    </div>
                    <div style={{display:"flex",flexWrap:"wrap",gap:10,fontSize:13,color:"#6b6356",alignItems:"center"}}>
                      <span style={{color:"#E8E0D0",fontWeight:600}}>{DEFAULT_EVENTS[bk.eventType]?.icon} {bk.eventType}</span>
                      <span>{fmtShort(bk.date)}</span>
                      <span>{fmtH(bk.start)} – {fmtH(bk.end)}</span>
                    </div>
                  </div>
                  <div style={{display:"flex",alignItems:"center",gap:6,flexShrink:0,flexWrap:"wrap",justifyContent:"flex-end"}}>
                    <span style={{color:"#C9A84C",fontWeight:700,fontSize:16}}>&#8377;{bk.total?.toLocaleString("en-IN")}</span>
                    {!bk.cancelled&&<>
                      {bk.customerEmail&&<button onClick={()=>syncResendEmail(bk,gcalConfig,perfTypes)} style={{background:"none",border:"1px solid #5B9BD5",color:"#5B9BD5",padding:"5px 10px",borderRadius:6,cursor:"pointer",fontSize:11,fontFamily:"'Outfit',sans-serif",whiteSpace:"nowrap"}}>&#9993; Resend email</button>}
                      <button onClick={()=>setEditBkId(editBkId===bk.id?null:bk.id)} style={{background:editBkId===bk.id?"#1a1500":"none",border:`1px solid ${editBkId===bk.id?"#C9A84C":"#2a2a2a"}`,color:editBkId===bk.id?"#C9A84C":"#5a5244",padding:"5px 10px",borderRadius:6,cursor:"pointer",fontSize:11,fontFamily:"'Outfit',sans-serif"}}>
                        {editBkId===bk.id?"Close":"Reschedule"}
                      </button>
                      {confirmCancelId===bk.id?(
                        <div style={{display:"flex",alignItems:"center",gap:6}}>
                          <span style={{fontSize:12,color:"#B85450"}}>Cancel booking?</span>
                          <button onClick={()=>cancelBooking(bk.id)} style={{background:"#B85450",border:"none",color:"#fff",padding:"5px 10px",borderRadius:6,cursor:"pointer",fontSize:11,fontFamily:"'Outfit',sans-serif",fontWeight:600}}>Yes, cancel</button>
                          <button onClick={()=>setConfirmCancelId(null)} style={{background:"none",border:"1px solid #2a2a2a",color:"#5a5244",padding:"5px 8px",borderRadius:6,cursor:"pointer",fontSize:11,fontFamily:"'Outfit',sans-serif"}}>No</button>
                        </div>
                      ):(
                        <button onClick={()=>setConfirmCancelId(bk.id)} style={{background:"none",border:"1px solid #9a4a3a",color:"#9a4a3a",padding:"5px 10px",borderRadius:6,cursor:"pointer",fontSize:11,fontFamily:"'Outfit',sans-serif"}}>Cancel booking</button>
                      )}
                    </>}
                    {confirmDeleteId===bk.id?(
                      <div style={{display:"flex",alignItems:"center",gap:6}}>
                        <span style={{fontSize:12,color:"#B85450"}}>Sure?</span>
                        <button onClick={()=>deleteBooking(bk.id)} style={{background:"#B85450",border:"none",color:"#fff",padding:"5px 10px",borderRadius:6,cursor:"pointer",fontSize:11,fontFamily:"'Outfit',sans-serif",fontWeight:600}}>Yes, delete</button>
                        <button onClick={()=>setConfirmDeleteId(null)} style={{background:"none",border:"1px solid #2a2a2a",color:"#5a5244",padding:"5px 8px",borderRadius:6,cursor:"pointer",fontSize:11,fontFamily:"'Outfit',sans-serif"}}>No</button>
                      </div>
                    ):(
                      <button onClick={()=>setConfirmDeleteId(bk.id)} style={{background:"none",border:"1px solid #B85450",color:"#B85450",padding:"5px 10px",borderRadius:6,cursor:"pointer",fontSize:11,fontFamily:"'Outfit',sans-serif"}}>Delete</button>
                    )}
                  </div>
                </div>
                <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10,marginBottom:bk.eventDescription?10:0}}>
                  <div style={{background:"#0a0a0a",borderRadius:6,padding:"10px 12px"}}>
                    <div style={{fontSize:10,color:"#3a3530",letterSpacing:"0.08em",textTransform:"uppercase",marginBottom:6}}>Customer</div>
                    <div style={{fontSize:13,color:"#E8E0D0",marginBottom:3}}>{bk.customerName}</div>
                    <div style={{fontSize:12,color:"#6b6356"}}>{bk.customerEmail}</div>
                    <div style={{fontSize:12,color:"#6b6356"}}>{bk.customerPhone}</div>
                  </div>
                  {bk.billing&&(
                    <div style={{background:"#0a0a0a",borderRadius:6,padding:"10px 12px"}}>
                      <div style={{fontSize:10,color:"#3a3530",letterSpacing:"0.08em",textTransform:"uppercase",marginBottom:6}}>Billing</div>
                      <div style={{fontSize:13,color:"#E8E0D0",marginBottom:3}}>{bk.billing.name}</div>
                      <div style={{fontSize:12,color:"#6b6356",lineHeight:1.5}}>{bk.billing.address}</div>
                      {bk.billing.gstNo&&<div style={{fontSize:11,color:"#C9A84C",marginTop:4}}>GST: {bk.billing.gstNo}</div>}
                    </div>
                  )}
                </div>
                {bk.discountCode&&(
                  <div style={{background:"#0d1a0f",border:"1px solid #1e3820",borderRadius:6,padding:"10px 12px",marginBottom:8}}>
                    <div style={{fontSize:10,color:"#2a6838",letterSpacing:"0.08em",textTransform:"uppercase",marginBottom:6}}>Promo applied</div>
                    <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",flexWrap:"wrap",gap:8}}>
                      <div style={{fontSize:13,color:"#6BAE75",fontWeight:600}}>Code: {bk.discountCode}</div>
                      <div style={{fontSize:13,color:"#6b6356"}}>
                        Original: <span style={{color:"#c0b8a8"}}>₹{bk.originalTotal?.toLocaleString("en-IN")}</span>
                        &nbsp;→&nbsp;Discount: <span style={{color:"#6BAE75"}}>−₹{bk.discountAmount?.toLocaleString("en-IN")}</span>
                        &nbsp;→&nbsp;Paid: <span style={{color:"#C9A84C",fontWeight:700}}>₹{bk.total?.toLocaleString("en-IN")}</span>
                      </div>
                    </div>
                  </div>
                )}
                {bk.eventDescription&&<div style={{background:"#0a0a0a",borderRadius:6,padding:"10px 12px",marginBottom:8}}><div style={{fontSize:10,color:"#3a3530",letterSpacing:"0.08em",textTransform:"uppercase",marginBottom:6}}>Event description</div><div style={{fontSize:13,color:"#9a8e7e",lineHeight:1.6}}>{bk.eventDescription}</div></div>}
                {bk.cancelled&&bk.cancelledAt&&<div style={{fontSize:11,color:"#B85450",marginBottom:6}}>Cancelled on {new Date(bk.cancelledAt).toLocaleString("en-IN")}</div>}
                <div style={{fontSize:11,color:"#3a3530",display:"flex",alignItems:"center",justifyContent:"space-between"}}>
                  <span>{new Date(bk.bookedAt).toLocaleString("en-IN")}</span>
                  {!bk.cancelled&&<a href={makeGCalUrl(bk)} target="_blank" rel="noreferrer" style={{fontSize:11,color:"#5B9BD5",textDecoration:"none",display:"flex",alignItems:"center",gap:4}}>📅 Add to Calendar</a>}
                </div>
                {editBkId===bk.id&&<BookingEditor booking={bk} allBookings={bookings} onSave={saveBookingEdit} onCancel={()=>setEditBkId(null)}/>}
              </div>
            ))}
                      </div>
        )}

        {tab==="settings"&&(
          <div>
            {adminH3("Settings")}
            <p style={{fontSize:13,color:"#5a5244",marginBottom:28,lineHeight:1.7}}>Manage your admin account and integrations.</p>
            {/* Change password */}
            <div style={{maxWidth:440,marginBottom:32}}>
              <div style={{background:"#141414",border:"1px solid #2a2a2a",borderRadius:10,padding:24}}>
                 <div style={{fontSize:13,fontWeight:700,color:"#E8E0D0",marginBottom:4}}>Change Admin password</div>
                <div style={{fontSize:12,color:"#5a5244",marginBottom:20,lineHeight:1.6}}>You'll need to use the new password on your next login.</div>
                {[["Current password",curPwd,setCurPwd],["New password (min 6 characters)",newPwd,setNewPwd],["Confirm new password",confirmPwd,setConfirmPwd]].map(([pl,val,set])=>(
                  <div key={pl}>
                    <div style={{fontSize:11,color:"#5a5244",marginBottom:6,letterSpacing:"0.06em",textTransform:"uppercase"}}>{pl}</div>
                    <input type="password" placeholder={pl} value={val} onChange={e=>{set(e.target.value);setPwdMsg(null);}} style={S.inp}/>
                  </div>
                ))}
                {pwdMsg&&<div style={{fontSize:13,color:pwdMsg.ok?"#6BAE75":"#B85450",marginBottom:14,display:"flex",alignItems:"center",gap:6}}><span>{pwdMsg.ok?"✓":"✗"}</span>{pwdMsg.text}</div>}
                <button onClick={async()=>{
                  if(!curPwd||!newPwd||!confirmPwd) return setPwdMsg({ok:false,text:"Please fill in all fields."});
                  if(curPwd!==storedPwd) return setPwdMsg({ok:false,text:"Current password is incorrect."});
                  if(newPwd.length<6) return setPwdMsg({ok:false,text:"New password must be at least 6 characters."});
                  if(newPwd!==confirmPwd) return setPwdMsg({ok:false,text:"New passwords do not match."});
                  await saveData("vb_pwd",newPwd); setStoredPwd(newPwd); setCurPwd(""); setNewPwd(""); setConfirmPwd("");
                  setPwdMsg({ok:true,text:"Password updated successfully."});
                }} style={{...S.btnP,width:"100%"}}>Update Admin password</button>
              </div>
            </div>

            {/* Manager password — Admin only */}
            <div style={{maxWidth:440,marginBottom:32}}>
              <div style={{background:"#141414",border:"1px solid #2a2a2a",borderRadius:10,padding:24}}>
                <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:4}}>
                  <span style={{fontSize:13,fontWeight:700,color:"#E8E0D0"}}>Change Manager password</span>
                  <span style={{fontSize:10,color:"#6BAE75",border:"1px solid #6BAE75",padding:"1px 7px",borderRadius:8,letterSpacing:"0.06em"}}>ADMIN ONLY</span>
                </div>
                <div style={{fontSize:12,color:"#5a5244",marginBottom:20,lineHeight:1.6}}>Set the password for the Manager login. Default is <code style={{color:"#c0b8a8"}}>manager@2024</code>.</div>
                {[["Current Manager password",mgrCurPwd,setMgrCurPwd],["New password (min 6 characters)",mgrNewPwd,setMgrNewPwd],["Confirm new password",mgrConfirmPwd,setMgrConfirmPwd]].map(([pl,val,set])=>(
                  <div key={pl}>
                    <div style={{fontSize:11,color:"#5a5244",marginBottom:6,letterSpacing:"0.06em",textTransform:"uppercase"}}>{pl}</div>
                    <input type="password" placeholder={pl} value={val} onChange={e=>{set(e.target.value);setMgrPwdMsg(null);}} style={S.inp}/>
                  </div>
                ))}
                {mgrPwdMsg&&<div style={{fontSize:13,color:mgrPwdMsg.ok?"#6BAE75":"#B85450",marginBottom:14,display:"flex",alignItems:"center",gap:6}}><span>{mgrPwdMsg.ok?"✓":"✗"}</span>{mgrPwdMsg.text}</div>}
                <button onClick={async()=>{
                  if(!mgrCurPwd||!mgrNewPwd||!mgrConfirmPwd) return setMgrPwdMsg({ok:false,text:"Please fill in all fields."});
                  if(mgrCurPwd!==storedManagerPwd) return setMgrPwdMsg({ok:false,text:"Current Manager password is incorrect."});
                  if(mgrNewPwd.length<6) return setMgrPwdMsg({ok:false,text:"New password must be at least 6 characters."});
                  if(mgrNewPwd!==mgrConfirmPwd) return setMgrPwdMsg({ok:false,text:"New passwords do not match."});
                  await saveData("vb_pwd_manager",mgrNewPwd); setStoredManagerPwd(mgrNewPwd); setMgrCurPwd(""); setMgrNewPwd(""); setMgrConfirmPwd("");
                  setMgrPwdMsg({ok:true,text:"Manager password updated successfully."});
                }} style={{...S.btnP,width:"100%"}}>Update Manager password</button>
              </div>
            </div>
            {/* Google Calendar */}
            <GCalSettings gcalConfig={gcalConfig} onSave={async cfg=>{await saveData("vb_gcal",cfg);onGcalChange(cfg);}}/>
          </div>
        )}

      </div>
    </div>
  );
}

// ── ROOT APP ──────────────────────────────────────────────────────────────────

export default function App() {
  const [isAdmin,setIsAdmin]=useState(()=>window.location.hash==="#admin");
  const [bookings,setBookings]=useState([]);
  const [blocked,setBlocked]=useState([]);
  const [facilities,setFacilities]=useState(()=>Object.fromEntries(Object.entries(DEFAULT_EVENTS).map(([k,v])=>[k,{included:[...v.included],excluded:[...v.excluded]}])));
  const [capacity,setCapacity]=useState(()=>DEFAULT_CAPACITY.map(c=>({...c})));
  const [pricing,setPricing]=useState(()=>Object.fromEntries(Object.entries(DEFAULT_EVENTS).map(([k,v])=>[k,{basePrice:v.basePrice,extraPerHour:v.extraPerHour}])));
  const [perfTypes,setPerfTypes]=useState(()=>DEFAULT_PERF_TYPES.map(p=>({...p})));
  const [tech,setTech]=useState(()=>Object.fromEntries(Object.entries(DEFAULT_TECH).map(([k,v])=>[k,[...v]])));
  const [gcalConfig,setGcalConfig]=useState({webhookUrl:"",calendarId:"",adminEmail:""});
  const [heroImage,setHeroImage]=useState("");
  const [logoImage,setLogoImage]=useState("");
  const [carouselImages,setCarouselImages]=useState([]);
  const [leads,setLeads]=useState([]);
  const [discounts,setDiscounts]=useState([]);
  const [bookingPrefix,setBookingPrefix]=useState("VB");
  const [bookingCounter,setBookingCounter]=useState(1);

  useEffect(()=>{
    // Load persisted data — updates state if custom values exist in storage
    loadData("vb_b").then(setBookings);
    loadData("vb_bl").then(setBlocked);
    loadFacilities().then(setFacilities);
    loadCapacity().then(setCapacity);
    loadPricing().then(setPricing);
    loadPerfTypes().then(setPerfTypes);
    loadTech().then(setTech);
    loadGCalConfig().then(setGcalConfig);
    loadData("vb_leads").then(setLeads);
    loadDiscounts().then(setDiscounts);
    loadData("vb_booking_counter",null).then(v=>{
      if(v&&typeof v==="object"&&!Array.isArray(v)){
        if(v.prefix&&typeof v.prefix==="string") setBookingPrefix(v.prefix.toUpperCase());
        if(v.counter&&typeof v.counter==="number"&&v.counter>0) setBookingCounter(v.counter);
      }
    });
    loadHeroImage().then(setHeroImage);
    loadLogoImage().then(setLogoImage);
    loadCarousel().then(setCarouselImages);
    const h=()=>setIsAdmin(window.location.hash==="#admin");
    window.addEventListener("hashchange",h);
    return ()=>window.removeEventListener("hashchange",h);
  },[]);

  const addBooking=async bk=>{
    const cfg=await loadData("vb_booking_counter",null);
    const prefix=(cfg&&cfg.prefix)?cfg.prefix.toUpperCase():(bookingPrefix||"VB");
    const counter=(cfg&&cfg.counter&&cfg.counter>0)?cfg.counter:(bookingCounter||1);
    const seqId=`${prefix}-${String(counter).padStart(3,"0")}`;
    await saveData("vb_booking_counter",{prefix,counter:counter+1});
    setBookingPrefix(prefix); setBookingCounter(counter+1);
    const bkWithId={...bk,id:seqId};
    const newBks=[...bookings,bkWithId];
    const newBls=applyBuffer(bkWithId,newBks,blocked);
    await saveData("vb_b",newBks);
    if(newBls.length!==blocked.length) await saveData("vb_bl",newBls);
    setBookings(newBks); setBlocked(newBls);
    syncToGCal(bkWithId,gcalConfig,perfTypes);
    const remainingLeads=leads.filter(l=>l.phone!==bkWithId.customerPhone);
    if(remainingLeads.length!==leads.length){
      await saveData("vb_leads",remainingLeads);
      setLeads(remainingLeads);
    }
    return bkWithId;
  };

  const saveLead=async lead=>{
    const updated=[...leads,lead];
    await saveData("vb_leads",updated);
    setLeads(updated);
  };

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:wght@400;500;600;700&family=Outfit:wght@300;400;500;600&display=swap');
        *,*::before,*::after{box-sizing:border-box;margin:0;padding:0;}
        body{background:#0a0a0a;color:#E8E0D0;font-family:'Outfit',sans-serif;}
        input,select,textarea{background:#141414;border:1px solid #2a2a2a;color:#E8E0D0;padding:10px 14px;border-radius:6px;font-size:14px;font-family:'Outfit',sans-serif;width:100%;margin-bottom:10px;outline:none;transition:border-color 0.2s;}
        input:focus,select:focus,textarea:focus{border-color:#C9A84C;}
        input::placeholder,textarea::placeholder{color:#3a3530;}
        textarea{resize:vertical;}
        option{background:#141414;}
      `}</style>
      {isAdmin
        ? <AdminApp onExit={()=>{window.location.hash="";setIsAdmin(false);}}
            facilities={facilities} onFacilitiesChange={setFacilities}
            capacity={capacity}    onCapacityChange={setCapacity}
            pricing={pricing}      onPricingChange={setPricing}
            perfTypes={perfTypes}  onPerfTypesChange={setPerfTypes}
            tech={tech}            onTechChange={setTech}
            onAppBookingsChange={setBookings} onAppBlockedChange={setBlocked}
            gcalConfig={gcalConfig} onGcalChange={setGcalConfig}
            heroImage={heroImage}  onHeroChange={setHeroImage}
            logoImage={logoImage}  onLogoChange={setLogoImage}
            carouselImages={carouselImages} onCarouselChange={setCarouselImages}
            leads={leads} onLeadsChange={setLeads}
            discounts={discounts} onDiscountsChange={setDiscounts}
            bookingPrefix={bookingPrefix} onBookingPrefixChange={setBookingPrefix}
            bookingCounter={bookingCounter} onBookingCounterChange={setBookingCounter}/>
        : <>
          <header style={{borderBottom:"1px solid #1a1a1a",padding:"15px 24px",background:"rgba(10,10,10,0.97)",position:"sticky",top:0,zIndex:100}}>
            <div style={{maxWidth:700,margin:"0 auto",display:"flex",alignItems:"center",gap:14}}>
              <div style={{display:"flex",alignItems:"center",gap:10}}>
                <span style={{color:"#C9A84C",fontSize:20}}>&#9672;</span>
                <span style={{fontFamily:"'Cormorant Garamond',serif",fontSize:22,fontWeight:600,letterSpacing:"0.14em"}}>Artkhoj Performance Space</span>
              </div>
              <span style={{flex:1,color:"#7a6e62",fontSize:11,letterSpacing:"0.1em",textTransform:"uppercase"}}>Premium Event Space &middot; Bengaluru</span>
            </div>
          </header>
          <UserApp bookings={bookings} blocked={blocked} facilities={facilities} capacity={capacity} pricing={pricing} perfTypes={perfTypes} tech={tech} addBooking={addBooking} saveLead={saveLead} heroImage={heroImage} logoImage={logoImage} carouselImages={carouselImages} discounts={discounts}/>
        </>
      }
    </>
  );
}
